import React, { useState, useEffect } from 'react';
import AuthImage, { AdminPhotoAccessContext } from '@/components/AuthImage';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Copy, Download, Users, Image, ChevronLeft, Calendar, Activity, Scale, Utensils, FileText, TrendingUp, FileDown, AlertCircle, Check, X, Loader2, Footprints, Flag, Database, Cloud, RefreshCw, HelpCircle, Unlock, Trash2, MessageSquare, GitMerge, Building, BadgeCheck, Phone, Mail, RotateCcw } from 'lucide-react';
import { getAllUsers, getAllFoodEntries, getUserFoodEntriesAdmin, getUserBiometricsAdmin, getUserDailyMacrosAdmin, getUserDailyMicrosAdmin, getAdminUserHealthFlags, getBackupStatus, triggerManualRestore, syncAllToCloudinary, getLoginHelpRequests, resolveLoginHelp, unlockUserFromHelp, deleteLoginHelp, getDeletedUserBackups, getSoftDeletedUsers, restoreUser, startPurgeTask, waitForAdminTask, getPurgeReports } from '@/utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import MyFlagsTab from '@/components/MyFlagsTab';
import FoodGallery from '@/components/FoodGallery';

// Extracted admin components
import { MealLogSummary, WeeklyTrend, AdminUserStepsView, AdminUserWeeklyReport } from '@/components/admin';
import { AdminPrivilegePanel } from '@/components/admin/AdminPrivilegePanel';
import { RetentionCard } from '@/components/admin/RetentionCard';
import { ResetAttemptsPanel, PostmarkCard } from '@/components/admin/ResetAttemptsPanel';
import { EmailFailureBanner, EmailFailuresView, MessageLookup } from '@/components/admin/EmailFailures';
import { Step5Panel } from '@/components/admin/Step5Panel';
import { PhotoLocationMetadataCard } from '@/components/admin/PhotoLocationMetadataCard';
import { MyActivityCard } from '@/components/admin/MyActivityCard';
import { SingleRunnerCard } from '@/components/admin/SingleRunnerCard';
import { AccessReviewCard } from '@/components/admin/AccessReviewCard';
import { AccessReviewBanner } from '@/components/admin/AccessReviewBanner';
import { ChangeRecordsCard } from '@/components/admin/ChangeRecordsCard';
import { DataFlowsCard } from '@/components/admin/DataFlowsCard';
import { DeletionProofCard } from '@/components/admin/DeletionProofCard';
import { AdminTasksCard } from '@/components/admin/AdminTasksCard';
import { DeletedAccountsPanel } from '@/components/admin/DeletedAccountsPanel';
import { Q7RemovalCard } from '@/components/admin/Q7RemovalCard';
import { CountryGateCard } from '@/components/admin/CountryGateCard';
import { formatAwst } from '@/utils/perthTime';
import { PendingDeletionBanner } from '@/components/admin/PendingDeletionBanner';
import { Stage2RecordsCard } from '@/components/admin/Stage2RecordsCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sub-components have been extracted to @/components/admin/
// - MealLogSummary: Weekly meal summary with PDF/Word export
// - WeeklyTrend: Weekly nutritional trend charts
// - AdminUserStepsView: Steps and heart rate history
// - AdminUserWeeklyReport: User weekly report view

// Progress bar component
const ProgressBar = ({ value, max, label, unit }) => {
  const safeValue = value ?? 0;
  const safeMax = max ?? 0;
  const percentage = safeMax > 0 ? Math.min((safeValue / safeMax) * 100, 150) : 0;
  const isExceeded = percentage > 100;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className={isExceeded ? 'text-red-500 font-bold' : 'text-muted-foreground'}>
          {safeValue.toFixed(1)} / {safeMax} {unit}
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${isExceeded ? 'bg-red-500' : 'bg-primary'}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

// User Feedback Tab component
const UserFeedbackTab = ({ userId, token }) => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/users/${userId}/feedback`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFeedback(response.data);
      } catch (error) {
        console.error('Failed to fetch feedback:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, [userId, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (feedback.length === 0) {
    return (
      <div className="bg-card rounded-2xl border border-border/50 p-8 text-center">
        <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">This user has not submitted any feedback yet.</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-primary" />
        User Feedback ({feedback.length})
      </h3>
      <div className="space-y-4">
        {feedback.map((item, index) => (
          <div key={item.id || index} className="bg-card rounded-xl border border-border/50 p-4">
            <div className="flex justify-between items-start mb-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                item.type === 'bug' ? 'bg-red-100 text-red-700' :
                item.type === 'feature' ? 'bg-blue-100 text-blue-700' :
                item.type === 'general' ? 'bg-gray-100 text-gray-700' :
                'bg-green-100 text-green-700'
              }`}>
                {item.type || 'General'}
              </span>
              <span className="text-xs text-muted-foreground">{formatDate(item.created_at)}</span>
            </div>
            <p className="text-sm">{item.message}</p>
            {item.rating && (
              <div className="mt-2 flex items-center gap-1">
                <span className="text-xs text-muted-foreground">Rating:</span>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className={`text-sm ${star <= item.rating ? 'text-yellow-500' : 'text-gray-300'}`}>★</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const matchesUserSearch = (u, q) => {
  const needle = (q || '').trim().toLowerCase();
  if (!needle) return true;
  return (u.email || '').toLowerCase().includes(needle) || (u.id || '').toLowerCase().startsWith(needle);
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  // Admin uses sessionStorage token, not regular auth
  // Use state so we can react to changes
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem('admin_session_token'));
  const [deletedPanelKey, setDeletedPanelKey] = useState(0);
  const [adminInfo, setAdminInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('entries');
  
  // User detail state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEntries, setUserEntries] = useState([]);
  const [userBiometrics, setUserBiometrics] = useState(null);
  const [userMacros, setUserMacros] = useState(null);
  const [userMicros, setUserMicros] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [userDetailTab, setUserDetailTab] = useState('food-diary');
  
  // Claims state
  const [claimRequests, setClaimRequests] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [processingClaim, setProcessingClaim] = useState(null);
  
  // Data Integrity state
  const [discrepancies, setDiscrepancies] = useState([]);
  const [loadingDiscrepancies, setLoadingDiscrepancies] = useState(false);
  const [processingDiscrepancy, setProcessingDiscrepancy] = useState(null);
  const [runningCheck, setRunningCheck] = useState(false);
  
  // Backup Management state
  const [backupStatus, setBackupStatus] = useState(null);
  const [loadingBackupStatus, setLoadingBackupStatus] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  
  // Login Help state
  const [loginHelpRequests, setLoginHelpRequests] = useState([]);
  const [loadingLoginHelp, setLoadingLoginHelp] = useState(false);
  const [processingHelp, setProcessingHelp] = useState(null);
  
  // User Feedback state
  const [feedbackList, setFeedbackList] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [processingFeedback, setProcessingFeedback] = useState(null);
  
  // Duplicate users state
  const [duplicateUsers, setDuplicateUsers] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [creatingEmailIndex, setCreatingEmailIndex] = useState(false);
  const [emailIndexResult, setEmailIndexResult] = useState(null);

  // Indexes view state
  const [dbCollections, setDbCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('users');
  const [collectionIndexes, setCollectionIndexes] = useState(null);
  const [loadingIndexes, setLoadingIndexes] = useState(false);
  const [mergingUsers, setMergingUsers] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [emailHealth, setEmailHealth] = useState(null);
  const [probeRunning, setProbeRunning] = useState(false);
  const [launchBlockers, setLaunchBlockers] = useState(null);
  const [lockoutActivity, setLockoutActivity] = useState(null);
  const [resetAttempts, setResetAttempts] = useState(null);
  const [emailFailures, setEmailFailures] = useState(null);
  const fetchEmailFailures = async (tok) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email/failures`, { headers: { Authorization: `Bearer ${tok || adminToken}` } });
      setEmailFailures(res.data);
    } catch (e) { /* non-blocking */ }
  };
  const acknowledgeEmailFailure = async (rowId) => {
    try {
      await axios.post(`${API_URL}/api/admin/email/failures/${rowId}/acknowledge`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success('Acknowledged');
      fetchEmailFailures();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not acknowledge'); }
  };
  const fetchResetAttempts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email/reset-attempts`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setResetAttempts(res.data);
    } catch (e) { /* non-blocking */ }
  };
  const [ttlDryRun, setTtlDryRun] = useState(null);
  const [ttlRunning, setTtlRunning] = useState(false);
  const [roleLock, setRoleLock] = useState(null);
  const [roleLockRunning, setRoleLockRunning] = useState(false);
  const runRoleLock = async () => {
    setRoleLockRunning(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/db-role-lock/run`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      setRoleLock(res.data);
      if (res.data.severity === 'ok') toast.success(`Database role lock ok: admin_activity add-only at the DB, ${res.data.collections_checked} collections probed, nothing written`);
      else if (res.data.severity === 'critical') toast.error(`Database role lock PROBLEM: ${res.data.problems[0]}`);
      else toast.warning(`Database role lock: ${res.data.problems[0]}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Database role lock probe failed to run');
    } finally {
      setRoleLockRunning(false);
    }
  };
  const [exposureRunning, setExposureRunning] = useState(false);
  const [artefactRunning, setArtefactRunning] = useState(false);
  const [artefactPlan, setArtefactPlan] = useState(null);

  const resendFailedAlerts = async () => {
    setExposureRunning(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/alerts/resend-failed`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast[res.data.resent?.every((r) => r.ok) ? 'success' : 'error'](`Re-sent ${res.data.resent?.length || 0} alert(s): ${res.data.resent?.map((r) => `${r.condition} → ${r.ok ? r.delivered_to : 'FAILED'}`).join('; ') || 'nothing pending'}`);
      fetchDiagnostics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Re-send failed');
    } finally {
      setExposureRunning(false);
    }
  };

  const runArtefactDeletion = async (dryRun) => {
    let confirm = '';
    if (!dryRun) {
      confirm = window.prompt(`This deletes the ${artefactPlan?.plan?.length || 0} assets shown in the dry-run plan from the PRODUCTION Cloudinary cloud. Nothing else is touched. Type DELETE_APPROVED to proceed.`);
      if (confirm !== 'DELETE_APPROVED') return;
    }
    setArtefactRunning(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/cloudinary/delete-approved-artefacts`, { dry_run: dryRun, confirm }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setArtefactPlan(res.data);
      toast[res.data.problems?.length ? 'warning' : 'success'](dryRun ? `Dry-run: ${res.data.plan.length} would be deleted, ${res.data.problems.length} problems` : `Deleted ${res.data.deleted.length} asset(s)`);
      if (!dryRun) fetchDiagnostics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Artefact deletion call failed');
    } finally {
      setArtefactRunning(false);
    }
  };

  const runExposureAudit = async () => {
    setExposureRunning(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/cloudinary-exposure/run`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast[res.data.status === 'CRITICAL' ? 'error' : 'success'](`Exposure audit: ${res.data.status} — ${res.data.public_assets} public, ${res.data.public_raw_count} public raw, ${res.data.public_meal_photo_count} public meal photos, ${res.data.flagged_count} flagged`);
      fetchDiagnostics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Exposure audit failed');
    } finally {
      setExposureRunning(false);
    }
  };

  const runTtlDryRun = async () => {
    setTtlRunning(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/backup-ttl/dry-run`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      setTtlDryRun(res.data);
      if (res.data.aborted_reason) toast.warning(`Dry-run aborted: ${res.data.aborted_reason}`);
      else if (res.data.error) toast.error(`Dry-run errored: ${res.data.error}`);
      else toast.success(`Dry-run complete: ${res.data.orphan_count} orphans would be deleted, ${res.data.expired_count} expired (>${res.data.expired_max_age_days}d) reported, nothing was deleted`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dry-run failed to run');
    } finally {
      setTtlRunning(false);
    }
  };

  const runExpiredPurge = async (count) => {
    const typed = window.prompt(`This deletes ${count} backup asset(s) of EXISTING users last written more than the max age ago. Only the assets listed in the reviewed dry-run are eligible; each is re-checked live. Type PURGE_EXPIRED to proceed.`);
    if (typed !== 'PURGE_EXPIRED') return;
    setTtlRunning(true);
    try {
      const started = await axios.post(`${API_URL}/api/admin/backup-ttl/purge-expired`, { confirm: typed }, { headers: { Authorization: `Bearer ${adminToken}` } });
      const done = await waitForAdminTask(adminToken, started.data.id); // Q8-2: background job with a progress row (Diagnostics → Background jobs)
      if (done.status !== 'completed') throw new Error(done.error || 'expired purge failed');
      const res = { data: done.result };
      setTtlDryRun(res.data);
      if (res.data.aborted_reason) toast.warning(`Purge aborted: ${res.data.aborted_reason}`);
      else if (res.data.error) toast.error(`Purge errored: ${res.data.error}`);
      else toast.success(`Expired purge: ${res.data.removed_count} removed, ${res.data.skipped?.length || 0} skipped`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Expired purge failed');
    } finally {
      setTtlRunning(false);
    }
  };

  const fetchLockoutActivity = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/lockout-activity`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setLockoutActivity(res.data);
    } catch (e) { /* non-blocking */ }
  };

  const fetchLaunchBlockers = async (tok) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/launch-blockers`, { headers: { Authorization: `Bearer ${tok || adminToken}` } });
      setLaunchBlockers(res.data);
    } catch (e) { /* non-blocking */ }
  };

  const [bounces, setBounces] = useState(null);
  const fetchBounces = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email/bounces`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setBounces(res.data);
    } catch (e) { /* non-blocking */ }
  };

  const fetchEmailHealth = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-health`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setEmailHealth(res.data);
      fetchBounces();
    } catch (e) { /* non-blocking */ }
  };

  const runEmailProbe = async () => {
    setProbeRunning(true);
    try {
      await axios.post(`${API_URL}/api/admin/email-health/run`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      await fetchEmailHealth();
      toast.success('Probe complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Probe failed to run');
    } finally {
      setProbeRunning(false);
    }
  };

  const [cutoverTo, setCutoverTo] = useState('');
  const [cutoverBusy, setCutoverBusy] = useState(false);
  const [cutoverResult, setCutoverResult] = useState(null);
  const sendCutoverTest = async () => {
    if (!cutoverTo.includes('@')) { toast.error('Enter a real mailbox you control'); return; }
    if (!window.confirm(`Send ONE real Postmark cutover test to ${cutoverTo}?\n\nNever use a made-up @insulnutrients.com address — unmatched addresses bounce and damage reputation.`)) return;
    setCutoverBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/email/cutover-test`, { to: cutoverTo, confirm: true }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setCutoverResult(res.data);
      if (res.data.ok) toast.success(`Postmark accepted · MessageID ${res.data.message_id}`); else toast.error(`Refused/failed: ${res.data.error?.message || 'see result'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cutover test failed');
    } finally {
      setCutoverBusy(false);
    }
  };
  const [outboundLookup, setOutboundLookup] = useState(null);
  const lookupOutbound = async (messageId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email/outbound/${messageId}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setOutboundLookup(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Lookup failed');
    }
  };

  const [diagError, setDiagError] = useState(null);
  const fetchDiagnostics = async () => {
    setDiagLoading(true);
    setDiagError(null);
    const t0 = Date.now();
    try {
      const res = await axios.get(`${API_URL}/api/admin/diagnostics`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setDiagData(res.data);
      fetchEmailHealth();
      fetchLockoutActivity();
      fetchResetAttempts();
      fetchJobLedger();
      fetchStuckEntries();
    } catch (error) {
      const msg = `GET /api/admin/diagnostics failed after ${Math.round((Date.now() - t0) / 1000)}s — HTTP ${error.response?.status ?? 'no response'}: ${error.response?.data?.detail || error.message}`;
      setDiagError(msg);
      toast.error(msg);
    } finally {
      setDiagLoading(false);
    }
  };

  const [stuckReport, setStuckReport] = useState(null);
  const [stuckBusy, setStuckBusy] = useState(null);
  const [stuckResult, setStuckResult] = useState(null);
  const fetchStuckEntries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/stuck-entries`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setStuckReport(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Stuck-entry report failed');
    }
  };
  const resolveStuckBucket = async (bucket, action, entries) => {
    const ids = entries.map((e) => e.entry_id);
    if (!window.confirm(`${action.toUpperCase()} ${ids.length} entr${ids.length === 1 ? 'y' : 'ies'} in bucket "${bucket}"?\n\nEach entry is re-classified server-side and refused if its bucket changed. This is a REAL action.`)) return;
    setStuckBusy(`${bucket}:${action}`);
    try {
      const res = await axios.post(`${API_URL}/api/admin/stuck-entries/resolve`, { bucket, action, entry_ids: ids, confirm: true }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setStuckResult(res.data);
      toast.success(`Sweep: ${JSON.stringify(res.data.summary)} · ${res.data.remaining_stuck_total} still stuck`);
      fetchStuckEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sweep action failed');
    } finally {
      setStuckBusy(null);
    }
  };

  const [jobLedger, setJobLedger] = useState(null);
  const [runNowBusy, setRunNowBusy] = useState(null);
  const fetchJobLedger = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/job-ledger`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setJobLedger(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Job ledger failed to load');
    }
  };
  const runJobNow = async (jobId) => {
    if (!window.confirm(`Ask the scheduler to fire "${jobId}" now? This has the same REAL effects as the daily run (not a dry-run).`)) return;
    setRunNowBusy(jobId);
    try {
      const res = await axios.post(`${API_URL}/api/admin/jobs/${jobId}/run-now`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(`Scheduler will fire ${jobId} at ${String(res.data.will_fire_at).slice(11, 19)} UTC — refreshing ledger in 15s`);
      setTimeout(fetchJobLedger, 15000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Run-now request failed');
    } finally {
      setRunNowBusy(null);
    }
  };
  
  // Soft-deleted users state
  const [softDeletedUsers, setSoftDeletedUsers] = useState([]);
  const [loadingSoftDeleted, setLoadingSoftDeleted] = useState(false);
  const [restoringUser, setRestoringUser] = useState(null);
  const [purgingUser, setPurgingUser] = useState(null);
  const [purgeProgress, setPurgeProgress] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  // Director 13 Oct 2026: the Users search matches an e-mail fragment or an account id prefix (e.g. 7a315057)
  const visibleUsers = users.filter((u) => matchesUserSearch(u, userSearch));
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [lastPurgeReport, setLastPurgeReport] = useState(null);
  const [userToPurge, setUserToPurge] = useState(null);
  
  
  // Delete user confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  
  // Cloudinary orphan backups state (read-only)
  const [orphanBackups, setOrphanBackups] = useState([]);
  const [loadingOrphanBackups, setLoadingOrphanBackups] = useState(false);

  useEffect(() => {
    // Re-read token from sessionStorage in case it was just updated
    const currentToken = sessionStorage.getItem('admin_session_token');
    if (currentToken !== adminToken) {
      setAdminToken(currentToken);
    }
    
    if (!currentToken) {
      navigate('/admin-login');
      return;
    }
    
    // Fetch admin info from session endpoint
    const fetchAdminInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin-auth/session`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        setAdminInfo(response.data.admin);
        fetchEmailFailures(currentToken);
      } catch (error) {
        console.error('Failed to fetch admin info:', error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          sessionStorage.removeItem('admin_session_token');
          navigate('/admin-login');
        }
      }
    };
    
    fetchAdminInfo();
    fetchData();
    fetchLaunchBlockers(currentToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchData = async () => {
    // Re-read token in case it was updated
    const currentToken = sessionStorage.getItem('admin_session_token');
    if (!currentToken) {
      console.error('No admin token available');
      navigate('/admin-login');
      return;
    }
    
    try {
      const [usersData, entriesData] = await Promise.all([
        getAllUsers(currentToken),
        getAllFoodEntries(currentToken)
      ]);
      setUsers(usersData);
      setEntries(entriesData);
      getSoftDeletedUsers(currentToken).then((d) => setSoftDeletedUsers(d.deleted_users || [])).catch(() => {});  // header "total incl. soft-deleted"
      
      // Fetch claim requests
      fetchClaimRequests();
    } catch (error) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        sessionStorage.removeItem('admin_session_token');
        navigate('/admin-login');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchClaimRequests = async () => {
    const currentToken = sessionStorage.getItem('admin_session_token');
    if (!currentToken) return;
    
    try {
      setLoadingClaims(true);
      const response = await axios.get(`${API_URL}/api/admin/claim-requests?status=all`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setClaimRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch claim requests:', error);
    } finally {
      setLoadingClaims(false);
    }
  };
  
  const handleApproveClaim = async (claimId) => {
    try {
      setProcessingClaim(claimId);
      await axios.post(`${API_URL}/api/admin/claim-requests/${claimId}/approve`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Claim approved! User account has been updated.');
      fetchClaimRequests();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve claim');
    } finally {
      setProcessingClaim(null);
    }
  };
  
  const handleRejectClaim = async (claimId) => {
    try {
      setProcessingClaim(claimId);
      await axios.post(`${API_URL}/api/admin/claim-requests/${claimId}/reject`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Claim rejected');
      fetchClaimRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject claim');
    } finally {
      setProcessingClaim(null);
    }
  };
  
  const fetchDiscrepancies = async () => {
    try {
      setLoadingDiscrepancies(true);
      const response = await axios.get(`${API_URL}/api/admin/data-integrity/discrepancies?status=all`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      // Filter out integrity_check summaries - only keep actual discrepancy records with status field
      const actualDiscrepancies = (response.data || []).filter(d => d.status && d.id);
      setDiscrepancies(actualDiscrepancies);
    } catch (error) {
      console.error('Failed to fetch discrepancies:', error);
    } finally {
      setLoadingDiscrepancies(false);
    }
  };
  
  const handleRunIntegrityCheck = async () => {
    try {
      setRunningCheck(true);
      const response = await axios.post(`${API_URL}/api/admin/data-integrity/run-check`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(response.data.message);
      fetchDiscrepancies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run integrity check');
    } finally {
      setRunningCheck(false);
    }
  };
  
  const handleApproveRecovery = async (discrepancyId) => {
    try {
      setProcessingDiscrepancy(discrepancyId);
      const response = await axios.post(`${API_URL}/api/admin/data-integrity/discrepancies/${discrepancyId}/approve`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(response.data.message);
      fetchDiscrepancies();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve recovery');
    } finally {
      setProcessingDiscrepancy(null);
    }
  };
  
  const handleDismissDiscrepancy = async (discrepancyId) => {
    try {
      setProcessingDiscrepancy(discrepancyId);
      await axios.post(`${API_URL}/api/admin/data-integrity/discrepancies/${discrepancyId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Discrepancy dismissed');
      fetchDiscrepancies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dismiss discrepancy');
    } finally {
      setProcessingDiscrepancy(null);
    }
  };

  // Backup Management functions
  const fetchBackupStatus = async () => {
    setLoadingBackupStatus(true);
    try {
      const status = await getBackupStatus(adminToken);
      setBackupStatus(status);
    } catch (error) {
      toast.error('Failed to fetch backup status');
    } finally {
      setLoadingBackupStatus(false);
    }
  };

  const handleSyncToCloudinary = async () => {
    setSyncing(true);
    try {
      const result = await syncAllToCloudinary(adminToken);
      toast.success(`Synced ${result.synced.users} users, ${result.synced.biometrics} biometrics, ${result.synced.food_entries} food entries`);
      fetchBackupStatus();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleRestoreFromCloudinary = async () => {
    setRestoring(true);
    try {
      const result = await triggerManualRestore(adminToken);
      toast.success(`Restored ${result.restored.users} users, ${result.restored.biometrics} biometrics, ${result.restored.food_entries} food entries`);
      fetchBackupStatus();
      fetchData(); // Refresh main data
    } catch (error) {
      toast.error('Restore failed');
    } finally {
      setRestoring(false);
    }
  };
  
  // Soft-Deleted Users functions
  const fetchSoftDeletedUsers = async () => {
    setLoadingSoftDeleted(true);
    try {
      const data = await getSoftDeletedUsers(adminToken);
      setSoftDeletedUsers(data.deleted_users || []);
      // Restore the last persisted purge report (survives page refresh)
      try {
        const rep = await getPurgeReports(adminToken, 1);
        if (rep.reports?.length) {
          const r = rep.reports[0];
          setLastPurgeReport({ email: r.email, at: r.purged_at, ...r });
        }
      } catch (e) { /* purge reports optional */ }
    } catch (error) {
      toast.error('Failed to fetch deleted users');
    } finally {
      setLoadingSoftDeleted(false);
    }
  };
  
  const handleRestoreUser = async (userId, userEmail) => {
    setRestoringUser(userId);
    try {
      await restoreUser(adminToken, userId);
      toast.success(`User ${userEmail} has been restored`);
      setSoftDeletedUsers(prev => prev.filter(u => u.id !== userId));
      fetchData(); // Refresh active users list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user');
    } finally {
      setRestoringUser(null);
    }
  };
  
  const [photoReasons, setPhotoReasons] = useState({});

  const copyText = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement('textarea'); ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); const ok = document.execCommand('copy'); document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand copy refused');
      }
      toast.success('User ID copied');
    } catch (e) { toast.error('Copy failed - select the ID and copy manually'); }
  };

  const initiatePurge = (user) => {
    setUserToPurge(user);
    setPurgeDialogOpen(true);
  };
  
  const confirmPurge = async () => {
    if (!userToPurge) return;
    
    setPurgingUser(userToPurge.id);
    try {
      const task = await startPurgeTask(adminToken, userToPurge.id);
      const done = await waitForAdminTask(adminToken, task.id, (t) => setPurgeProgress(t.progress));
      if (done.status !== 'completed') throw new Error(done.error || 'purge failed');
      const report = done.result;
      const moved = report?.mongo_deleted || {};
      const total = Object.values(moved).reduce((a, b) => a + b, 0);
      setLastPurgeReport({ user_id: userToPurge.id, at: new Date().toISOString(), ...report });
      toast.success(`${userToPurge.email} permanently purged — ${total} database docs + ${(report?.cloudinary?.photos_deleted || 0) + (report?.cloudinary?.prefix_assets_deleted || 0)} Cloudinary assets destroyed. Itemized report shown below.`);
      setSoftDeletedUsers(prev => prev.filter(u => u.id !== userToPurge.id));
      setPurgeDialogOpen(false);
      setUserToPurge(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purge user');
    } finally {
      setPurgingUser(null);
      setPurgeProgress(null);
    }
  };
  
  // Orphan Cloudinary Backups (read-only info)
  const fetchOrphanBackups = async () => {
    setLoadingOrphanBackups(true);
    try {
      const data = await getDeletedUserBackups(adminToken);
      setOrphanBackups(data.deleted_backups || []);
    } catch (error) {
      toast.error('Failed to fetch orphan backups');
    } finally {
      setLoadingOrphanBackups(false);
    }
  };

  // Login Help functions
  const fetchLoginHelpRequests = async () => {
    setLoadingLoginHelp(true);
    try {
      const data = await getLoginHelpRequests(adminToken);
      setLoginHelpRequests(data.requests || []);
    } catch (error) {
      toast.error('Failed to fetch login help requests');
    } finally {
      setLoadingLoginHelp(false);
    }
  };

  const handleResolveHelp = async (requestId) => {
    setProcessingHelp(requestId);
    try {
      await resolveLoginHelp(adminToken, requestId, 'Resolved by admin');
      toast.success('Request marked as resolved');
      fetchLoginHelpRequests();
    } catch (error) {
      toast.error('Failed to resolve request');
    } finally {
      setProcessingHelp(null);
    }
  };

  const handleUnlockFromHelp = async (requestId) => {
    setProcessingHelp(requestId);
    try {
      await unlockUserFromHelp(adminToken, requestId);
      toast.success('User account unlocked');
      fetchLoginHelpRequests();
    } catch (error) {
      toast.error('Failed to unlock account');
    } finally {
      setProcessingHelp(null);
    }
  };

  const handleDeleteHelp = async (requestId) => {
    setProcessingHelp(requestId);
    try {
      await deleteLoginHelp(adminToken, requestId);
      toast.success('Request deleted');
      fetchLoginHelpRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    } finally {
      setProcessingHelp(null);
    }
  };

  // Feedback functions
  const fetchFeedback = async () => {
    setLoadingFeedback(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/feedback`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setFeedbackList(response.data);
    } catch (error) {
      toast.error('Failed to fetch feedback');
    } finally {
      setLoadingFeedback(false);
    }
  };

  const handleReviewFeedback = async (feedbackId, status, response = null) => {
    setProcessingFeedback(feedbackId);
    try {
      let url = `${API_URL}/api/admin/feedback/${feedbackId}/review?status=${status}`;
      if (response) {
        url += `&response=${encodeURIComponent(response)}`;
      }
      await axios.put(url, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Feedback marked as ${status}`);
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to update feedback');
    } finally {
      setProcessingFeedback(null);
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;
    setProcessingFeedback(feedbackId);
    try {
      await axios.delete(`${API_URL}/api/admin/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Feedback deleted');
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to delete feedback');
    } finally {
      setProcessingFeedback(null);
    }
  };

  // Duplicate users functions
  const fetchIndexesFor = async (collection) => {
    setLoadingIndexes(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/db-indexes?collection=${encodeURIComponent(collection)}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCollectionIndexes(response.data.indexes);
    } catch (error) {
      toast.error('Failed to fetch indexes');
      setCollectionIndexes(null);
    } finally {
      setLoadingIndexes(false);
    }
  };

  const fetchDbCollections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/db-collections`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setDbCollections(response.data.collections);
      fetchIndexesFor(selectedCollection);
    } catch (error) {
      toast.error('Failed to fetch collections');
    }
  };

  const handleCreateUniqueEmailIndex = async () => {
    setCreatingEmailIndex(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/create-unique-email-index`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setEmailIndexResult(response.data);
      toast.success(`Unique email index created: ${response.data.index_created}`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to create index';
      setEmailIndexResult({ error: detail });
      toast.error(detail);
    } finally {
      setCreatingEmailIndex(false);
    }
  };

  const fetchDuplicateUsers = async () => {
    setLoadingDuplicates(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/duplicate-users`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setDuplicateUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch duplicate users');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleMergeUsers = async (primaryUserId, secondaryUserId, primaryEmail, secondaryEmail) => {
    setMergingUsers(true);
    try {
      // Step 1: dry-run preview (no writes)
      const dryRun = await axios.post(
        `${API_URL}/api/admin/merge-users`,
        { primary_user_id: primaryUserId, secondary_user_id: secondaryUserId, dry_run: true },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const p = dryRun.data;
      const moveLines = (p.move_plan || [])
        .filter(m => m.secondary_docs_to_move > 0)
        .map(m => `  • ${m.collection}: ${m.secondary_docs_to_move} docs will move`);
      const singletonLines = (p.singleton_plan || []).map(s => `  • ${s.collection}: ${s.resolution.replaceAll('_', ' ')}`);
      const fillFields = Object.keys(p.profile_fields_to_fill_from_secondary || {});
      const summary = [
        `MERGE PREVIEW (nothing has been changed yet)`,
        ``,
        `"${secondaryEmail}" → "${primaryEmail}"`,
        `PRIMARY (survives): ${p.primary_user?.id} (created ${p.primary_user?.created_at?.slice(0, 10)})`,
        `SECONDARY (merged): ${p.secondary_user?.id} (created ${p.secondary_user?.created_at?.slice(0, 10)})`,
        ``,
        moveLines.length ? `Data to move:\n${moveLines.join('\n')}` : `Data to move: none — all ${(p.move_plan || []).length ? '18' : '18'} user-data collections checked, secondary has 0 documents in every one`,
        singletonLines.length ? `\nSingle-record conflicts:\n${singletonLines.join('\n')}` : '',
        fillFields.length ? `\nProfile fields filled from secondary: ${fillFields.join(', ')}` : '',
        ``,
        `A full snapshot of "${secondaryEmail}" will be archived first (reversible), then the account is soft-deleted and blocked from login.`,
        ``,
        `Execute this merge now?`
      ].filter(Boolean).join('\n');
      
      if (!window.confirm(summary)) {
        toast.info('Merge cancelled — nothing was changed.');
        return;
      }
      
      // Step 2: execute
      const response = await axios.post(
        `${API_URL}/api/admin/merge-users`,
        { primary_user_id: primaryUserId, secondary_user_id: secondaryUserId, dry_run: false },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const moved = response.data.documents_moved || {};
      const total = Object.values(moved).reduce((a, b) => a + b, 0);
      toast.success(`Merged ${secondaryEmail} into ${primaryEmail} — ${total} documents moved, snapshot archived (${response.data.merge_id?.slice(0, 8)})`);
      fetchDuplicateUsers();
      fetchData(); // Refresh user list
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to merge users';
      toast.error(message);
    } finally {
      setMergingUsers(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    // Open confirmation dialog instead of window.confirm
    setUserToDelete({ id: userId, email: userEmail });
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/admin/users/${userToDelete.id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(response.data.message);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData(); // Refresh user list
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    } finally {
      setDeletingUser(false);
    }
  };

  const handleUserClick = async (clickedUser) => {
    setSelectedUser(clickedUser);
    setLoadingUserData(true);
    setUserDetailTab('food-diary');
    
    // Get current token
    const currentToken = sessionStorage.getItem('admin_session_token');
    if (!currentToken) {
      toast.error('Session expired. Please login again.');
      navigate('/admin-login');
      return;
    }
    
    try {
      const [entriesData, biometricsData] = await Promise.all([
        getUserFoodEntriesAdmin(clickedUser.id, currentToken),
        getUserBiometricsAdmin(clickedUser.id, currentToken).catch(() => null)
      ]);
      
      setUserEntries(entriesData);
      setUserBiometrics(biometricsData);
      
      if (biometricsData) {
        await fetchDailyNutrition(clickedUser.id, selectedDate);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        sessionStorage.removeItem('admin_session_token');
        navigate('/admin-login');
      } else {
        toast.error('Failed to load user data');
      }
    } finally {
      setLoadingUserData(false);
    }
  };

  const fetchDailyNutrition = async (userId, date) => {
    const currentToken = sessionStorage.getItem('admin_session_token');
    if (!currentToken) return;
    
    try {
      const [macrosData, microsData] = await Promise.all([
        getUserDailyMacrosAdmin(userId, date, currentToken).catch(() => null),
        getUserDailyMicrosAdmin(userId, date, currentToken).catch(() => null)
      ]);
      setUserMacros(macrosData);
      setUserMicros(microsData);
    } catch (error) {
      console.error('Error fetching daily nutrition:', error);
    }
  };

  const handleDateChange = async (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    if (selectedUser && userBiometrics) {
      await fetchDailyNutrition(selectedUser.id, newDate);
    }
  };

  const handleBackToUsers = () => {
    setSelectedUser(null);
    setUserEntries([]);
    setUserBiometrics(null);
    setUserMacros(null);
    setUserMicros(null);
  };

  const handleDownload = (imageUrl, entryId) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `meal-${entryId}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMealTypeLabel = (type) => {
    const labels = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack', drink: 'Drink', late_night: 'Late Night' };
    return labels[type] || type;
  };

  const getMealTypeColor = (type) => {
    const colors = {
      breakfast: 'bg-amber-500/20 text-amber-600',
      lunch: 'bg-green-500/20 text-green-600',
      dinner: 'bg-purple-500/20 text-purple-600',
      snack: 'bg-pink-500/20 text-pink-600',
      drink: 'bg-blue-500/20 text-blue-600',
      late_night: 'bg-indigo-500/20 text-indigo-600'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-600';
  };

  // Admin session is already verified by AdminRoute
  if (!adminToken) return null;

  // User detail view
  if (selectedUser) {
    return (
      <AdminPhotoAccessContext.Provider value={{ token: adminToken }}>
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-transparent">
        <nav className="border-b border-border/50 bg-card/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                data-testid="back-to-users-button"
                onClick={handleBackToUsers}
                className="p-2 hover:bg-muted rounded-full transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
                <span className="text-lg text-muted-foreground">| User Details</span>
              </div>
            </div>
          </div>
        </nav>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loadingUserData ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading user data...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* User Info Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card rounded-2xl border border-border/50 p-6"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <Users className="w-8 h-8 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold break-all" data-testid="selected-user-email">{selectedUser.email}</h2>
                    <p className="text-muted-foreground">
                      Joined {formatDate(selectedUser.created_at)} · {userEntries.length} meals logged
                    </p>
                    <p className="text-xs font-mono text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                      <span>ID</span>
                      <span className="select-all break-all" data-testid="selected-user-id">{selectedUser.id}</span>
                      <button type="button" onClick={() => copyText(selectedUser.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 hover:bg-muted transition-colors" data-testid="copy-user-id-btn">
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Tab Navigation */}
              <div className="flex gap-2 border-b border-border/50 overflow-x-auto">
                {[
                  { id: 'food-diary', label: 'Food Diary', icon: Image },
                  { id: 'food-summary', label: 'My Food Summary', icon: FileText },
                  { id: 'biometrics', label: 'Biometrics', icon: Scale },
                  { id: 'feedback', label: 'Feedback', icon: MessageSquare }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setUserDetailTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-all border-b-2 -mb-[1px] whitespace-nowrap ${
                      userDetailTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <motion.div
                key={userDetailTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                {userDetailTab === 'food-diary' && (
                  <div>
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Image className="w-5 h-5 text-primary" />
                      Food Diary ({userEntries.length} entries)
                    </h3>
                    {userEntries.length > 0 ? (
                      <FoodGallery 
                        entries={userEntries} 
                        token={adminToken}
                        onEntryUpdate={() => handleUserClick(selectedUser)}
                      />
                    ) : (
                      <div className="bg-card rounded-2xl border border-border/50 p-8 text-center">
                        <p className="text-muted-foreground">This user has no meal entries yet.</p>
                      </div>
                    )}
                  </div>
                )}

                {userDetailTab === 'biometrics' && (
                  <div className="space-y-6">
                    {/* Biometrics & Nutrition Section */}
                    <div className="bg-card rounded-2xl border border-border/50 p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <Scale className="w-5 h-5 text-primary" />
                          Biometrics & Daily Nutrition
                        </h3>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <input
                            type="date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            className="bg-muted border border-border rounded-lg px-3 py-2 text-sm"
                            data-testid="nutrition-date-picker"
                          />
                        </div>
                      </div>

                      {userBiometrics ? (
                        <div className="grid md:grid-cols-2 gap-8">
                          <div className="space-y-4">
                            <h4 className="font-semibold text-lg flex items-center gap-2">
                              <Scale className="w-4 h-4" />
                              Body Metrics
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="bg-muted/50 rounded-xl p-4">
                                <p className="text-sm text-muted-foreground">Weight</p>
                                <p className="text-2xl font-bold">{userBiometrics.weight_kg} <span className="text-sm font-normal">kg</span></p>
                              </div>
                              <div className="bg-muted/50 rounded-xl p-4">
                                <p className="text-sm text-muted-foreground">Height</p>
                                <p className="text-2xl font-bold">{userBiometrics.height_cm} <span className="text-sm font-normal">cm</span></p>
                              </div>
                              <div className="bg-muted/50 rounded-xl p-4">
                                <p className="text-sm text-muted-foreground">Waist</p>
                                <p className="text-2xl font-bold">{userBiometrics.waist_cm} <span className="text-sm font-normal">cm</span></p>
                              </div>
                              <div className="bg-muted/50 rounded-xl p-4">
                                <p className="text-sm text-muted-foreground">BMI</p>
                                <p className="text-2xl font-bold">{userBiometrics.bmi}</p>
                              </div>
                              <div className="bg-muted/50 rounded-xl p-4">
                                <p className="text-sm text-muted-foreground">Ideal Body Weight</p>
                                <p className="text-2xl font-bold">{userBiometrics.ideal_body_weight} <span className="text-sm font-normal">kg</span></p>
                              </div>
                              {userBiometrics.adjusted_body_weight && (
                                <div className="bg-muted/50 rounded-xl p-4">
                                  <p className="text-sm text-muted-foreground">Adjusted Weight</p>
                                  <p className="text-2xl font-bold">{userBiometrics.adjusted_body_weight} <span className="text-sm font-normal">kg</span></p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="space-y-4">
                            <h4 className="font-semibold text-lg flex items-center gap-2">
                              <Utensils className="w-4 h-4" />
                              Daily Macros ({selectedDate})
                            </h4>
                            {userMacros ? (
                              <div className="space-y-4">
                                <ProgressBar value={userMacros.total_protein} max={userMacros.target_protein} label="Protein" unit="g" />
                                <ProgressBar value={userMacros.total_carbs} max={userMacros.target_carbs} label="Carbohydrates" unit="g" />
                                <ProgressBar value={userMacros.total_fat} max={userMacros.target_fat} label="Fat" unit="g" />
                              </div>
                            ) : (
                              <p className="text-muted-foreground text-sm">No macro data for this date</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">User has not set up biometrics yet.</p>
                      )}

                      {userMicros && userBiometrics && (
                        <div className="mt-8 pt-6 border-t border-border">
                          <h4 className="font-semibold text-lg mb-4">Daily Micronutrients ({selectedDate})</h4>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <ProgressBar value={userMicros.vitamin_a_mcg} max={800} label="Vitamin A" unit="mcg" />
                            <ProgressBar value={userMicros.vitamin_c_mg} max={82.5} label="Vitamin C" unit="mg" />
                            <ProgressBar value={userMicros.folate_mcg} max={400} label="Folate" unit="mcg" />
                            <ProgressBar value={userMicros.vitamin_b12_mcg} max={2.4} label="Vitamin B12" unit="mcg" />
                            <ProgressBar value={userMicros.calcium_mg} max={1100} label="Calcium" unit="mg" />
                            <ProgressBar value={userMicros.iron_mg} max={13} label="Iron" unit="mg" />
                            <ProgressBar value={userMicros.iodine_mcg} max={150} label="Iodine" unit="mcg" />
                            <ProgressBar value={userMicros.magnesium_mg} max={365} label="Magnesium" unit="mg" />
                            <ProgressBar value={userMicros.potassium_mg} max={3000} label="Potassium" unit="mg" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {userDetailTab === 'food-summary' && (
                  <div className="space-y-6">
                    {/* Weekly Meal Summary */}
                    <MealLogSummary 
                      userId={selectedUser.id}
                      token={adminToken}
                      userEmail={selectedUser.email}
                      userName={`${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`}
                    />
                    
                    {/* Weekly Trends */}
                    <WeeklyTrend userId={selectedUser.id} token={adminToken} />
                  </div>
                )}

                {userDetailTab === 'feedback' && (
                  <UserFeedbackTab userId={selectedUser.id} token={adminToken} />
                )}
              </motion.div>
            </div>
          )}
        </main>
      </div>
      </AdminPhotoAccessContext.Provider>
    );
  }

  // Main admin dashboard view
  return (
    <AdminPhotoAccessContext.Provider value={{ token: adminToken }}>
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-transparent">

      <EmailFailureBanner data={emailFailures} onOpen={() => setView('alerts')} />
      <PendingDeletionBanner token={adminToken} onOpen={() => setView('users')} />
      <AccessReviewBanner token={adminToken} onOpen={() => { setView('diagnostics'); fetchDiagnostics(); }} />
      <nav className="border-b border-border/50 bg-card/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-muted rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
              <span className="text-lg text-muted-foreground">| Admin</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Dashboard
            </h1>
            <div className="flex justify-center gap-8 text-lg">
              <div>
                <span className="font-bold text-primary">{users.length}</span> Users
                <span className="block text-xs text-muted-foreground" data-testid="users-total-incl-soft-deleted">{users.length + softDeletedUsers.length} total incl. {softDeletedUsers.length} soft-deleted</span>
              </div>
              <div>
                <span className="font-bold text-primary">{entries.length}</span> Meals
              </div>
            </div>
          </div>

          {launchBlockers && launchBlockers.count > 0 && (
            <div className="max-w-3xl mx-auto mb-8 rounded-2xl border-2 border-red-300 bg-red-50 p-5" data-testid="launch-blockers-panel">
              <p className="font-bold text-red-800 mb-2">
                🚧 {launchBlockers.count} open launch blocker{launchBlockers.count === 1 ? '' : 's'} — must all clear before real public launch
              </p>
              <ul className="space-y-2">
                {launchBlockers.blockers.map((b) => (
                  <li key={b.key} className="text-sm text-red-900" data-testid={`launch-blocker-${b.key}`}>
                    <span className="font-semibold">{b.label}</span>
                    <span className="block text-xs text-red-700/80 break-all">{b.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-center gap-4 mb-8 flex-wrap">
            <Button
              data-testid="view-alerts-tab"
              variant={view === 'alerts' ? 'default' : 'outline'}
              className={view === 'alerts'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-red-500 to-rose-600"
                : "rounded-full px-8 py-4 font-semibold border-2 border-red-500 text-red-600 hover:bg-red-50"
              }
              onClick={() => { setView('alerts'); fetchEmailFailures(); }}
            >
              <AlertCircle className="w-5 h-5 mr-2" />
              Alerts
              {emailFailures?.open_total > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white text-red-700 text-xs font-bold" data-testid="alerts-tab-badge">{emailFailures.open_total}</span>}
            </Button>
            <Button
              data-testid="view-diagnostics-tab"
              variant={view === 'diagnostics' ? 'default' : 'outline'}
              className={view === 'diagnostics'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-teal-500 to-cyan-500"
                : "rounded-full px-8 py-4 font-semibold border-2 border-teal-500 text-teal-600 hover:bg-teal-50"
              }
              onClick={() => { setView('diagnostics'); fetchDiagnostics(); }}
            >
              <Activity className="w-5 h-5 mr-2" />
              Diagnostics
            </Button>
            <Button
              data-testid="view-indexes-tab"
              variant={view === 'indexes' ? 'default' : 'outline'}
              className={view === 'indexes'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-slate-600 to-slate-800"
                : "rounded-full px-8 py-4 font-semibold border-2 border-slate-500 text-slate-600 hover:bg-slate-50"
              }
              onClick={() => { setView('indexes'); fetchDbCollections(); }}
            >
              <Database className="w-5 h-5 mr-2" />
              Indexes
            </Button>
            <Button
              data-testid="view-users-tab"
              variant={view === 'users' ? 'default' : 'outline'}
              className={view === 'users'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-primary to-[#8FD14F]"
                : "rounded-full px-8 py-4 font-semibold border-2 border-primary text-primary hover:bg-primary/10"
              }
              onClick={() => setView('users')}
            >
              <Users className="w-5 h-5 mr-2" />
              Users
            </Button>
            <Button
              data-testid="view-duplicates-tab"
              variant={view === 'duplicates' ? 'default' : 'outline'}
              className={view === 'duplicates'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-indigo-500 to-violet-500"
                : "rounded-full px-8 py-4 font-semibold border-2 border-indigo-500 text-indigo-600 hover:bg-indigo-50"
              }
              onClick={() => { setView('duplicates'); fetchDuplicateUsers(); }}
            >
              <GitMerge className="w-5 h-5 mr-2" />
              Duplicates
              {duplicateUsers.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {duplicateUsers.length}
                </span>
              )}
            </Button>
            <Button
              data-testid="view-claims-tab"
              variant={view === 'claims' ? 'default' : 'outline'}
              className={view === 'claims'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-amber-500 to-orange-500"
                : "rounded-full px-8 py-4 font-semibold border-2 border-amber-500 text-amber-600 hover:bg-amber-50"
              }
              onClick={() => { setView('claims'); fetchClaimRequests(); }}
            >
              <AlertCircle className="w-5 h-5 mr-2" />
              Claims
              {claimRequests.filter(c => c.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {claimRequests.filter(c => c.status === 'pending').length}
                </span>
              )}
            </Button>
            <Button
              data-testid="view-integrity-tab"
              variant={view === 'integrity' ? 'default' : 'outline'}
              className={view === 'integrity'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-blue-500 to-cyan-500"
                : "rounded-full px-8 py-4 font-semibold border-2 border-blue-500 text-blue-600 hover:bg-blue-50"
              }
              onClick={() => { setView('integrity'); fetchDiscrepancies(); }}
            >
              <Activity className="w-5 h-5 mr-2" />
              Data Integrity
              {discrepancies.filter(d => d.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {discrepancies.filter(d => d.status === 'pending').length}
                </span>
              )}
            </Button>
            <Button
              data-testid="view-backups-tab"
              variant={view === 'backups' ? 'default' : 'outline'}
              className={view === 'backups'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-purple-500 to-pink-500"
                : "rounded-full px-8 py-4 font-semibold border-2 border-purple-500 text-purple-600 hover:bg-purple-50"
              }
              onClick={() => { setView('backups'); fetchBackupStatus(); }}
            >
              <Cloud className="w-5 h-5 mr-2" />
              Backups
            </Button>
            <Button
              data-testid="view-login-help-tab"
              variant={view === 'login-help' ? 'default' : 'outline'}
              className={view === 'login-help'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-amber-500 to-orange-500"
                : "rounded-full px-8 py-4 font-semibold border-2 border-amber-500 text-amber-600 hover:bg-amber-50"
              }
              onClick={() => { setView('login-help'); fetchLoginHelpRequests(); }}
            >
              <HelpCircle className="w-5 h-5 mr-2" />
              Login Help
              {loginHelpRequests.filter(r => !r.resolved).length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {loginHelpRequests.filter(r => !r.resolved).length}
                </span>
              )}
            </Button>
            <Button
              data-testid="view-feedback-tab"
              variant={view === 'feedback' ? 'default' : 'outline'}
              className={view === 'feedback'
                ? "rounded-full px-8 py-4 font-bold shadow-lg bg-gradient-to-r from-purple-500 to-pink-500"
                : "rounded-full px-8 py-4 font-semibold border-2 border-purple-500 text-purple-600 hover:bg-purple-50"
              }
              onClick={() => { setView('feedback'); fetchFeedback(); }}
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Feedback
              {feedbackList.filter(f => f.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {feedbackList.filter(f => f.status === 'pending').length}
                </span>
              )}
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading data...</p>
            </div>
          ) : (
            <div>
              {view === 'alerts' && (
                <div className="bg-card rounded-3xl border border-border/50 p-6" data-testid="alerts-view">
                  <EmailFailuresView data={emailFailures} onAck={acknowledgeEmailFailure} />
                </div>
              )}
              {view === 'diagnostics' && (
                <div className="bg-card rounded-3xl border border-border/50 shadow-lg p-8 mb-8" data-testid="diagnostics-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold">Production Diagnostics (read-only)</h2>
                    <Button onClick={fetchDiagnostics} disabled={diagLoading} className="rounded-full" data-testid="diagnostics-refresh-btn">
                      <RefreshCw className={`w-4 h-4 mr-2 ${diagLoading ? 'animate-spin' : ''}`} />
                      {diagLoading ? 'Running...' : 'Re-run'}
                    </Button>
                  </div>
                  <RetentionCard adminToken={adminToken} refreshKey={diagData?.generated_at || diagLoading} />
                  <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="email-health-panel">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                      <h3 className="font-bold text-lg">Email Health (Postmark, transactional stream)</h3>
                      <Button onClick={runEmailProbe} disabled={probeRunning} variant="outline" size="sm" className="rounded-full" data-testid="run-email-probe-btn">
                        <RefreshCw className={`w-4 h-4 mr-2 ${probeRunning ? 'animate-spin' : ''}`} />
                        {probeRunning ? 'Probing...' : 'Run probe now (→ blackhole)'}
                      </Button>
                    </div>
                    {emailHealth?.latest ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span
                            data-testid="email-health-status"
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${emailHealth.latest.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                          >
                            {emailHealth.latest.success ? 'Postmark accepting mail' : 'Postmark NOT accepting mail — no outbound path'}
                          </span>
                          <div className="text-sm text-muted-foreground">
                            <p>Last probe: {emailHealth.latest.checked_at?.slice(0, 19)} UTC ({emailHealth.latest.trigger})</p>
                            <p className="font-mono text-xs">
                              Last 7: {emailHealth.history.map((h) => (h.success ? '✓' : '✗')).join(' ')}
                            </p>
                          </div>
                        </div>
                        {emailHealth.latest.provider === 'postmark' ? (
                          <div className="grid sm:grid-cols-2 gap-2 text-sm">
                            <div className={`rounded-lg border p-2 ${emailHealth.latest.server_error || String(emailHealth.latest.environment_guard || '').startsWith('BLOCKED') ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`} data-testid="email-health-server">
                              <p className="font-semibold">Server: {emailHealth.latest.server_name || '(lookup failed)'} · {emailHealth.latest.delivery_type || '—'}</p>
                              <p className="font-mono text-xs">guard: {emailHealth.latest.environment_guard}</p>
                              {emailHealth.latest.server_error && <p className="text-xs text-red-700">{JSON.stringify(emailHealth.latest.server_error)}</p>}
                            </div>
                            <div className={`rounded-lg border p-2 ${emailHealth.latest.postmark_ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`} data-testid="email-health-postmark">
                              <p className="font-semibold">Probe send → {emailHealth.latest.probe_recipient}: {emailHealth.latest.postmark_ok ? 'ACCEPTED' : 'FAILED'}</p>
                              <p className="font-mono text-xs break-all">MessageID {emailHealth.latest.message_id || '—'} · Postmark status {emailHealth.latest.postmark_status || '—'}</p>
                              <p className="font-mono text-xs">From {emailHealth.latest.from} · Reply-To {emailHealth.latest.reply_to} · stream {emailHealth.latest.stream}</p>
                              {emailHealth.latest.postmark_error && <p className="text-xs text-red-700">{JSON.stringify(emailHealth.latest.postmark_error)}</p>}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">Last probe predates the Postmark cutover (legacy Gmail/SendGrid record). Run a new probe.</p>
                        )}
                        <p className="text-[11px] text-muted-foreground">Compares: {emailHealth.latest.compares || 'legacy record'}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground" data-testid="email-health-status">No probe has run yet. Daily at 23:30 WST, or run one now.</p>
                    )}
                    <div className="mt-4 rounded-lg border border-border/60 p-3" data-testid="cutover-test-panel">
                      <p className="font-semibold text-sm mb-1">Cutover confirmation — send one real message to a mailbox you control</p>
                      <p className="text-xs text-muted-foreground mb-2">Then open its original headers: <code>Authentication-Results</code> must show <code>dkim=pass header.d=insulnutrients.com</code>, <code>spf=pass</code>, <code>dmarc=pass</code>. Made-up @insulnutrients.com addresses are refused server-side.</p>
                      <div className="flex gap-2 flex-wrap">
                        <input value={cutoverTo} onChange={(e) => setCutoverTo(e.target.value)} placeholder="you@your-real-mailbox" className="border rounded-full px-3 py-1 text-sm flex-1 min-w-[240px]" data-testid="cutover-to-input" />
                        <Button onClick={sendCutoverTest} disabled={cutoverBusy} size="sm" className="rounded-full" data-testid="cutover-send-btn">{cutoverBusy ? 'Sending…' : 'Send cutover test'}</Button>
                      </div>
                      {cutoverResult && (
                        <div className="text-xs mt-2 font-mono break-all" data-testid="cutover-result">
                          <p>{cutoverResult.ok ? `ACCEPTED by ${cutoverResult.server} · MessageID ${cutoverResult.message_id}` : `NOT SENT — ${cutoverResult.error?.kind}: ${cutoverResult.error?.message}`}</p>
                          {cutoverResult.ok && <Button variant="link" size="sm" className="px-0 h-6" onClick={() => lookupOutbound(cutoverResult.message_id)} data-testid="cutover-lookup-btn">Fetch Postmark status/events for this MessageID</Button>}
                          {outboundLookup && <pre className="bg-background/70 rounded p-2 max-h-40 overflow-auto">{JSON.stringify(outboundLookup.postmark, null, 1)}</pre>}
                          <p className="text-muted-foreground font-sans">{cutoverResult.what_this_proves}</p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3" data-testid="email-health-scope-note">
                      Scope: a green probe means Postmark <strong>accepted</strong> a message on the transactional stream with the configured token, sender and Reply-To. It does <strong>not</strong> prove inbox delivery or receiver-side DKIM/DMARC — only headers from a received message do.
                    </p>
                    {bounces && (
                      <div className="mt-3 rounded-lg border border-border/60 p-3 text-sm" data-testid="email-bounces-panel">
                        <p className="font-semibold">Bounces &amp; deliveries (Postmark webhook) — {bounces.webhook_configured ? 'webhook credentials configured' : 'WEBHOOK NOT CONFIGURED (endpoint returns 503)'}</p>
                        <p className="font-mono text-xs">deliveries {bounces.deliveries_total} · hard bounces {bounces.hard_bounces_total} · users flagged email_bounced {bounces.users_flagged_bounced}</p>
                        {bounces.events.length > 0 ? (
                          <ul className="font-mono text-xs mt-1 max-h-32 overflow-auto">
                            {bounces.events.slice(0, 10).map((e) => <li key={e.id}>{String(e.received_at).slice(0, 16)} · {e.record_type}{e.bounce_type ? `/${e.bounce_type}` : ''} · {e.email} · {e.description || e.details || ''}</li>)}
                          </ul>
                        ) : <p className="text-xs text-muted-foreground mt-1">No webhook events received yet.</p>}
                        <p className="text-[11px] text-muted-foreground mt-1">Webhook URL shape: <code>{bounces.webhook_url_shape}</code></p>
                        <p className="text-[11px] text-muted-foreground">Compares: {bounces.compares}</p>
                      </div>
                    )}
                  </div>
                  <Step5Panel token={adminToken} />
                  <Stage2RecordsCard token={adminToken} />
                  <PhotoLocationMetadataCard token={adminToken} />
                  <DeletionProofCard token={adminToken} />
                  <AdminTasksCard token={adminToken} />
                  <MessageLookup token={adminToken} />
                  <ResetAttemptsPanel data={resetAttempts} token={adminToken} />
                  <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="lockout-activity-panel">
                    <h3 className="font-bold text-lg mb-2">Lockouts & Failed Logins</h3>
                    {lockoutActivity ? (
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs uppercase font-bold text-muted-foreground mb-1">Active lockouts</p>
                          {lockoutActivity.active_lockouts.length === 0 ? (
                            <p className="text-sm text-muted-foreground" data-testid="no-active-lockouts">None</p>
                          ) : (
                            <ul className="space-y-1">
                              {lockoutActivity.active_lockouts.map((lk) => (
                                <li key={lk.user_id} className="text-sm" data-testid={`lockout-${lk.user_id}`}>
                                  <span className="font-mono">{lk.email}</span> — locked until {String(lk.locked_until).slice(0, 16)} UTC
                                  {lk.pending_deletion && (
                                    <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-bold">
                                      PENDING DELETION — possible erasure-DoS target
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div>
                          <p className="text-xs uppercase font-bold text-muted-foreground mb-1">Failed attempts (7 days, by account)</p>
                          {lockoutActivity.failed_attempts_7d.length === 0 ? (
                            <p className="text-sm text-muted-foreground">None</p>
                          ) : (
                            <ul className="space-y-0.5">
                              {lockoutActivity.failed_attempts_7d.map((f, i) => (
                                <li key={i} className="text-sm font-mono">
                                  {f.email} · {f.count}× · {f.surface} · last {String(f.last_attempt).slice(0, 16)}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground italic">{lockoutActivity.note}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Loading…</p>
                    )}
                  </div>
                  {!diagData?.startup_checks && (
                    <div className={`rounded-2xl border p-5 mb-6 ${diagError ? 'border-red-300 bg-red-50' : 'border-border/60'}`} data-testid="startup-checks-placeholder">
                      <h3 className="font-bold text-lg mb-1">Startup Checks</h3>
                      <p className="text-sm" data-testid="startup-checks-state">
                        {diagLoading ? 'Loading — waiting for GET /api/admin/diagnostics (live Supabase, Postmark and Cloudinary lookups; can take 10–30s)…'
                          : diagError ? `NOT LOADED. ${diagError}. The admin-accounts card and its "Delete orphaned doc" button only render once this request succeeds. Press Re-run.`
                          : 'Not loaded yet — press Re-run.'}
                      </p>
                    </div>
                  )}
                  {diagData?.startup_checks && (() => {
                    const sc = diagData.startup_checks;
                    const ttl = sc.backup_ttl;
                    const adm = sc.admin_accounts;
                    const shownRun = ttlDryRun || ttl.last_run;
                    const feOk = sc.frontend_url.status === 'verified';
                    const ttlTone = !ttl.enabled ? 'border-slate-200 bg-slate-50' : ttl.dry_run && ttl.db_assertion_ok ? 'border-lime-200 bg-lime-50' : !ttl.db_assertion_ok ? 'border-amber-200 bg-amber-50' : 'border-red-300 bg-red-50';
                    return (<>
                      <AccessReviewCard token={adminToken} />
                      <ChangeRecordsCard token={adminToken} />
                      <MyActivityCard token={adminToken} />
                      <SingleRunnerCard token={adminToken} />
                      <DataFlowsCard token={adminToken} />
                      <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="startup-checks-panel">
                        <h3 className="font-bold text-lg mb-1">Startup Checks (live values — what the boot/daily log lines would say)</h3>
                        <p className="text-xs text-muted-foreground mb-3">Recomputed on every diagnostics run from the current environment and database, not read from logs.</p>
                        <div className="grid sm:grid-cols-2 gap-3 text-sm">
                          {sc.admin_session_secret && (
                            <div className={`rounded-lg border p-3 ${sc.admin_session_secret.ok ? 'border-green-200 bg-green-50' : 'border-red-300 bg-red-50'}`} data-testid="startup-check-admin-session-secret">
                              <p className="font-semibold">ADMIN_SESSION_SECRET — {sc.admin_session_secret.ok ? 'ok' : 'PROBLEM'} · fingerprint <span className="font-mono" data-testid="admin-session-secret-fingerprint">{sc.admin_session_secret.fingerprint_sha256_8 || '—'}</span> · length {sc.admin_session_secret.length}</p>
                              {sc.admin_session_secret.problems.map((p) => <p key={p} className="text-xs text-red-700">{p}</p>)}
                              <p className="text-[11px] text-muted-foreground mt-1">Compares: {sc.admin_session_secret.compares}</p>
                            </div>
                          )}
                          {sc.secrets_separation && (
                            <div className={`rounded-lg border p-3 ${sc.secrets_separation.ok ? 'border-green-200 bg-green-50' : 'border-red-300 bg-red-50'}`} data-testid="secrets-separation-card">
                              <p className="font-semibold">Secrets separation — {sc.secrets_separation.ok ? 'ok' : 'PROBLEM'} · environment <span className="font-mono">{sc.secrets_separation.environment}</span> by {sc.secrets_separation.detected_by} · FRONTEND_URL agrees: {String(sc.secrets_separation.frontend_url_agrees)}</p>
                              {sc.secrets_separation.problems.map((p) => <p key={p} className="text-xs text-red-700">{p}</p>)}
                              <ul className="text-xs font-mono mt-1" data-testid="secrets-split-keys">
                                {Object.entries(sc.secrets_separation.split_keys).map(([k, v]) => (
                                  <li key={k}>{k}: reads {v.reads} · preview fp {v.preview_key_fingerprint || '—'} · production-named key {v.production_key_present ? `present fp ${v.production_key_fingerprint}` : 'absent'}</li>
                                ))}
                              </ul>
                              <p className="text-xs mt-2 font-semibold">Production-only keys merely present in this environment ({sc.secrets_separation.production_only_keys_present_in_preview.length}):</p>
                          {(roleLock || sc.db_role_lock) && (() => {
                            const rl = roleLock || sc.db_role_lock;
                            const tone = rl.severity === 'ok' ? 'border-green-200 bg-green-50' : rl.severity === 'critical' ? 'border-red-300 bg-red-50' : rl.severity === 'info' ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50';
                            return (
                              <div className={`rounded-lg border p-3 ${tone}`} data-testid="db-role-lock-card">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="font-semibold" data-testid="db-role-lock-status">Database role lock — {rl.severity === 'ok' ? 'ok' : rl.severity === 'critical' ? 'PROBLEM' : rl.severity === 'info' ? 'not yet enforced' : 'not run'} · admin_activity locked at DB: <span className="font-mono">{String(rl.admin_activity_locked ?? '—')}</span></p>
                                  <button type="button" onClick={runRoleLock} disabled={roleLockRunning} className="shrink-0 rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-50" data-testid="db-role-lock-run-btn">
                                    {roleLockRunning ? 'Probing…' : 'Run now'}
                                  </button>
                                </div>
                                <p className="text-xs mt-1">Collections probed: <span className="font-mono">{rl.collections_checked ?? '—'}</span> · update allowed: <span className="font-mono">{rl.update_allowed ?? '—'}</span> · missing update grant: <span className="font-mono" data-testid="db-role-lock-missing">{(rl.missing_update_grant || []).length}</span> · wrote nothing: <span className="font-mono">{String(rl.wrote_nothing ?? '—')}</span> · checked {rl.checked_at ? rl.checked_at.slice(0, 19) + 'Z' : '—'} ({rl.trigger || '—'})</p>
                                {(rl.missing_update_grant || []).length > 0 && (
                                  <p className="text-xs font-mono text-red-700 mt-1" data-testid="db-role-lock-missing-list">{rl.missing_update_grant.join(', ')}</p>
                                )}
                                {(rl.problems || []).map((p) => <p key={p} className={`text-xs mt-1 ${rl.severity === 'critical' ? 'text-red-700' : 'text-amber-800'}`}>{p}</p>)}
                                {rl.compares && <p className="text-[11px] text-muted-foreground mt-1">Compares: {rl.compares}</p>}
                              </div>
                            );
                          })()}

                              <ul className="text-xs font-mono" data-testid="secrets-production-only-present">
                                {sc.secrets_separation.production_only_keys_present_in_preview.map((p) => <li key={p.key}>{p.key} · fp {p.fingerprint} · read by this code: {String(p.read_by_preview_code)}</li>)}
                              </ul>
                              {sc.secrets_separation.production_named_keys_in_env_file && (
                                <div className="mt-2" data-testid="secrets-env-file-rule">
                                  <p className="text-xs font-semibold">Production-named credentials written in this environment's .env file — flagged ({sc.secrets_separation.production_named_keys_in_env_file.flagged.length}) · known pending ({sc.secrets_separation.production_named_keys_in_env_file.known_pending.length}):</p>
                                  <ul className="text-xs font-mono">
                                    {sc.secrets_separation.production_named_keys_in_env_file.flagged.map((p) => <li key={p.key} className="text-red-700">FLAGGED {p.key} · fp {p.fingerprint}</li>)}
                                    {sc.secrets_separation.production_named_keys_in_env_file.known_pending.map((p) => <li key={p.key} className="text-amber-700">{p.key} · fp {p.fingerprint} · {p.reason}</li>)}
                                  </ul>
                                  <p className="text-[11px] text-muted-foreground">Rule: {sc.secrets_separation.production_named_keys_in_env_file.rule}</p>
                                </div>
                              )}
                              <p className="text-[11px] text-muted-foreground mt-1">Compares: {sc.secrets_separation.compares}</p>
                            </div>
                          )}
                          {sc.login_allowlist && (
                            <div className="rounded border p-2 text-xs" data-testid="login-allowlist-card">
                              <p className="font-semibold">Login allowlist (LOGIN_ALLOWLIST): {sc.login_allowlist.set ? `${sc.login_allowlist.count} entr${sc.login_allowlist.count === 1 ? 'y' : 'ies'}` : 'not set (lockdown off)'}</p>
                              <p className="font-mono">{sc.login_allowlist.entry_fingerprints.join(' · ') || '—'}</p>
                              <p className="text-[11px] text-muted-foreground">{sc.login_allowlist.note}</p>
                            </div>
                          )}
                          {sc.auto_grace_purge_ledger && (
                            <div className={`rounded-lg border p-3 ${sc.auto_grace_purge_ledger.status === 'completed' ? 'border-green-200 bg-green-50' : sc.auto_grace_purge_ledger.recorded ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'}`} data-testid="startup-check-auto-grace-purge">
                              <p className="font-semibold">Auto grace purge — last ledger row: {sc.auto_grace_purge_ledger.recorded ? `${sc.auto_grace_purge_ledger.status} · ${String(sc.auto_grace_purge_ledger.last_run_at).slice(0, 16)}Z` : 'none recorded yet'}</p>
                              {sc.auto_grace_purge_ledger.recorded && (
                                <p className="font-mono text-xs" data-testid="auto-grace-purge-summary">expired {sc.auto_grace_purge_ledger.expired ?? '—'} · purged {sc.auto_grace_purge_ledger.purged ?? '—'} · failed {sc.auto_grace_purge_ledger.failed ?? '—'} · deferred (lockout) {sc.auto_grace_purge_ledger.deferred_locked ?? '—'} · receipts {sc.auto_grace_purge_ledger.receipts_sent ?? '—'}{sc.auto_grace_purge_ledger.error ? ` · error: ${sc.auto_grace_purge_ledger.error}` : ''}</p>
                              )}
                              <p className="text-[11px] text-muted-foreground mt-1">Compares: {sc.auto_grace_purge_ledger.compares}</p>
                            </div>
                          )}
                          {sc.supabase_key_in_use && (
                            <div className={`rounded-lg border p-3 ${sc.supabase_key_in_use.class.startsWith('sb_secret') ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`} data-testid="startup-check-supabase-key">
                              <p className="font-semibold">Supabase key loaded: {sc.supabase_key_in_use.class}</p>
                              <p className="font-mono text-xs">{sc.supabase_key_in_use.env_var} = {sc.supabase_key_in_use.prefix || '(unset)'}</p>
                              <p className="text-[11px] text-muted-foreground mt-1">Compares: {sc.supabase_key_in_use.compares}</p>
                            </div>
                          )}
                          {adm && (
                            <div className={`rounded-lg border p-3 sm:col-span-2 ${adm.password_only_admins.length ? 'border-red-300 bg-red-50' : 'border-green-200 bg-green-50'}`} data-testid="startup-check-admin-accounts">
                              <p className="text-[11px] text-muted-foreground" data-testid="admin-accounts-read-at">rows below read at {String(diagData.generated_at).slice(11, 19)}Z (GET /api/admin/diagnostics)</p>
                              <p className="font-semibold">Admin accounts in this database: {adm.count} — <span data-testid="admin-accounts-active">{adm.active} active</span>, <span data-testid="admin-accounts-orphaned">{adm.orphaned} orphaned</span>{adm.unverified ? `, ${adm.unverified} unverified` : ''} · password-only among active: <span data-testid="password-only-admins-count">{adm.password_only_admins.length}</span></p>
                              <ul className="font-mono text-xs mt-1">
                                {adm.accounts.map((a) => <li key={a.email} className={a.status.startsWith('ORPHANED') ? 'text-amber-800' : a.status.startsWith('unverified') ? 'text-muted-foreground' : ''}>{a.email} · <span className="font-semibold">{a.status}</span>{a.supabase_email && a.email_matches_supabase === false ? ` (Supabase says ${a.supabase_email}!)` : ''} · mfa_enrolled={String(a.mfa_enrolled)} · mfa_secret_set={String(a.mfa_secret_set)} · backup_codes={String(a.backup_codes_set)} · supabase {a.auth_provider_id} · last login {String(a.last_login || '—').slice(0, 16)}
                                  {a.status.startsWith('ORPHANED') && (
                                    <Button size="sm" variant="destructive" className="rounded-full h-6 text-[11px] ml-2" data-testid={`delete-admin-account-${a.email}`}
                                      onClick={async () => {
                                        if (!window.confirm(`Delete the admin_accounts document for ${a.email}? Live Supabase lookup says no user exists for it. Refused if you are that account or if no other active admin would remain.`)) return;
                                        try {
                                          const res = await axios.post(`${API_URL}/api/admin/accounts/delete`, { email: a.email, confirm: true }, { headers: { Authorization: `Bearer ${adminToken}` } });
                                          toast.success(`Deleted ${res.data.deleted} · remaining docs ${res.data.remaining_docs} · active admins: ${res.data.remaining_active_admins.join(', ')}`);
                                          fetchDiagnostics();
                                        } catch (error) { toast.error(error.response?.data?.detail || 'Delete refused'); }
                                      }}>Delete orphaned doc</Button>
                                  )}
                                </li>)}
                              </ul>
                              <p className="text-[11px] text-muted-foreground mt-1">Compares: {adm.compares}</p>
                              {sc.supabase_signup && (
                                <p className={`text-xs mt-2 font-semibold ${sc.supabase_signup.ok ? 'text-green-800' : 'text-red-700'}`} data-testid="startup-check-supabase-signup">
                                  Supabase self-service registration: disable_signup = {String(sc.supabase_signup.disable_signup)}{sc.supabase_signup.error ? ` (error: ${sc.supabase_signup.error})` : ''} — register R-1 {sc.supabase_signup.ok ? 'holding' : 'BROKEN'} · checked {String(sc.supabase_signup.checked_at).slice(11, 19)}Z
                                </p>
                              )}
                              {sc.admin_privilege && (
                                <p className="text-xs mt-1" data-testid="startup-check-admin-privilege">
                                  Count watermark: {sc.admin_privilege.count_watermark ? `${sc.admin_privilege.count_watermark.count} (set ${String(sc.admin_privilege.count_watermark.updated_at).slice(0, 16)}Z by ${sc.admin_privilege.count_watermark.trigger || '?'})` : 'not yet recorded'}
                                  {sc.admin_privilege.count_watermark && (
                                    <span data-testid="count-watermark-emails"> · accounts at last observation: {Array.isArray(sc.admin_privilege.count_watermark.emails) ? (sc.admin_privilege.count_watermark.emails.length ? <span className="font-mono">{sc.admin_privilege.count_watermark.emails.join(', ')}</span> : '(none)') : <span className="text-amber-700">not recorded (watermark written before 24 Sep; the next change will name accounts)</span>}</span>
                                  )} · alerts → <span className="font-mono">{sc.admin_privilege.alert_recipient}</span>
                                </p>
                              )}
                              <AdminPrivilegePanel adminToken={adminToken} onChanged={fetchDiagnostics} refreshKey={diagData.generated_at} accountsReadAt={diagData.generated_at} />
                            </div>
                          )}
                          <div className={`rounded-lg border p-3 ${feOk ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`} data-testid="startup-check-frontend-url">
                            <p className="font-semibold">FRONTEND_URL — {sc.frontend_url.status}</p>
                            <p className="font-mono text-xs break-all">actual: {sc.frontend_url.FRONTEND_URL}</p>
                            <p className="font-mono text-xs break-all">expected: {sc.frontend_url.FRONTEND_URL_EXPECTED}</p>
                            <p className="text-[11px] text-muted-foreground mt-1">Compares: {sc.frontend_url.compares}</p>
                          </div>
                          <div className={`rounded-lg border p-3 ${sc.legacy_cancellation_links.count ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`} data-testid="startup-check-legacy-links">
                            <p className="font-semibold">Legacy cancellation links: <span data-testid="legacy-links-count">{sc.legacy_cancellation_links.count}</span> pending deletion(s) holding dead links</p>
                            {sc.legacy_cancellation_links.accounts.length > 0 && (
                              <ul className="font-mono text-xs mt-1">
                                {sc.legacy_cancellation_links.accounts.map((a) => <li key={a.email}>{a.email} · deleted_at {String(a.deleted_at).slice(0, 10)}</li>)}
                              </ul>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">Heal is manual: {sc.legacy_cancellation_links.heal_endpoint}</p>
                            <p className="text-[11px] text-muted-foreground mt-1">Compares: {sc.legacy_cancellation_links.compares}</p>
                          </div>
                          <div className="rounded-lg border border-orange-200 bg-orange-50 p-3" data-testid="startup-check-consents">
                            <p className="font-semibold">Pre-release consents pending re-consent: {sc.pre_release_consents.pending}</p>
                            <p className="text-[11px] text-muted-foreground mt-1">Compares: {sc.pre_release_consents.compares}</p>
                          </div>
                          {sc.users_missing_is_admin && (
                            <div className={`rounded-lg border p-3 ${sc.users_missing_is_admin.count ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`} data-testid="startup-check-missing-is-admin">
                              <p className="font-semibold">Users missing is_admin field: <span data-testid="missing-is-admin-count">{sc.users_missing_is_admin.count}</span></p>
                              {sc.users_missing_is_admin.emails.length > 0 && <p className="font-mono text-xs">{sc.users_missing_is_admin.emails.join(', ')}</p>}
                              <p className="text-[11px] text-muted-foreground mt-1">Compares: {sc.users_missing_is_admin.compares}</p>
                            </div>
                          )}
                          {sc.auto_finalization_stuck && (
                            <div className={`rounded-lg border p-3 sm:col-span-2 ${sc.auto_finalization_stuck.count ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`} data-testid="startup-check-stuck-entries">
                              <p className="font-semibold">Unfinalized entries over 48h: <span data-testid="stuck-entries-count">{sc.auto_finalization_stuck.count}</span></p>
                              {Object.keys(sc.auto_finalization_stuck.breakdown).length > 0 && (
                                <ul className="font-mono text-xs mt-1">
                                  {Object.entries(sc.auto_finalization_stuck.breakdown).map(([k, v]) => <li key={k}>{v} × {k}</li>)}
                                </ul>
                              )}
                              {sc.auto_finalization_stuck.samples.length > 0 && (
                                <ul className="text-xs mt-2 space-y-1 max-h-40 overflow-auto bg-white/60 rounded p-2">
                                  {sc.auto_finalization_stuck.samples.map((s) => <li key={s.entry_id}><span className="font-mono">{s.entry_id.slice(0, 8)}</span> · {String(s.created_at).slice(0, 10)}{s.photo_deleted ? ' · photo deleted' : ''} — {s.reason}</li>)}
                                </ul>
                              )}
                              <p className="text-[11px] text-muted-foreground mt-1">Compares: {sc.auto_finalization_stuck.compares}</p>
                            </div>
                          )}
                          {sc.alerts && (() => {
                            const al = sc.alerts; const failing = al.delivery_failing || !al.destination_check?.ok;
                            return (
                              <div className={`rounded-lg border p-3 sm:col-span-2 ${failing ? 'border-red-400 bg-red-50' : al.open?.length ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`} data-testid="startup-check-alerts">
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                  <p className="font-semibold">Alerts: {al.open?.length || 0} open · destination {al.destination || 'UNSET'}</p>
                                  {failing && <Button onClick={resendFailedAlerts} disabled={exposureRunning} variant="destructive" size="sm" className="rounded-full" data-testid="alerts-resend-btn">Re-send failed alerts</Button>}
                                </div>
                                {failing && (
                                  <p className="text-red-700 font-bold mt-1" data-testid="alerts-delivery-failing">
                                    ALERT DELIVERY FAILING — {al.destination_check?.refusal || al.delivery_failing_detail}
                                  </p>
                                )}
                                <p className="text-xs mt-1" data-testid="alerts-destination-check">Destination check at boot: {al.destination_check?.ok ? 'passes recipient guard' : `REFUSED: ${al.destination_check?.refusal}`}{al.destination_check?.fallback ? ` · fallback ${al.destination_check.fallback}${al.destination_check.fallback_refusal ? ' (also refused)' : ''}` : ' · no fallback configured'}</p>
                                {al.open?.length > 0 && (
                                  <ul className="text-xs font-mono mt-1 bg-white/60 rounded p-2" data-testid="alerts-open-list">
                                    {al.open.map((o) => <li key={o._id}>[{o.tier}] {o._id} · since {String(o.since).slice(0, 19)}Z · {o.found}</li>)}
                                  </ul>
                                )}
                                <p className="text-[11px] text-muted-foreground mt-1">Last heartbeat: {al.last_heartbeat ? `${String(al.last_heartbeat.sent_at).slice(0, 19)}Z · delivered=${String(al.last_heartbeat.delivery?.ok)}` : 'none yet'} · Compares: {al.compares}</p>
                              </div>
                            );
                          })()}
                          {sc.postmark && <PostmarkCard pm={sc.postmark} />}
                          {sc.contact_form && (
                            <div className={`rounded-lg border p-3 ${sc.contact_form.ok ? 'border-green-200 bg-green-50' : 'border-red-400 bg-red-50'}`} data-testid="startup-check-contact-form">
                              <p className="font-semibold">Contact form recipient: {sc.contact_form.destination || 'UNSET'}</p>
                              <p className="text-xs mt-1" data-testid="contact-form-check">{sc.contact_form.ok ? 'passes recipient guard' : `REFUSED: ${sc.contact_form.refusal}`}</p>
                            </div>
                          )}
                          {sc.cloudinary_exposure && (() => {
                            const ce = sc.cloudinary_exposure; const lr = ce.last_run;
                            const tone = ce.status === 'CRITICAL' ? 'border-red-300 bg-red-50' : ce.status === 'ok' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50';
                            return (
                              <div className={`rounded-lg border p-3 sm:col-span-2 ${tone}`} data-testid="startup-check-cloudinary-exposure">
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                  <p className="font-semibold">Cloudinary public exposure: <span data-testid="cloudinary-exposure-status">{ce.status}</span></p>
                                  <Button onClick={runExposureAudit} disabled={exposureRunning} variant="outline" size="sm" className="rounded-full" data-testid="run-cloudinary-exposure-btn">
                                    <RefreshCw className={`w-4 h-4 mr-2 ${exposureRunning ? 'animate-spin' : ''}`} />{exposureRunning ? 'Enumerating...' : 'Run audit now'}
                                  </Button>
                                </div>
                                {lr ? (
                                  <div className="text-xs mt-1" data-testid="cloudinary-exposure-summary">
                                    <p>{lr.trigger} · {String(lr.ran_at).slice(0, 19)} UTC · {lr.total_assets} assets · <span data-testid="cloudinary-public-count">{lr.public_assets}</span> publicly deliverable · {lr.non_public_assets} private/authenticated · <span data-testid="cloudinary-public-raw-count">{lr.public_raw_count}</span> public RAW · <span data-testid="cloudinary-public-meal-photo-count">{lr.public_meal_photo_count ?? 'n/a (pre-D-13 run)'}</span> public MEAL PHOTOS (must be 0) · {lr.flagged_count} flagged outside allow-list · {lr.cloudinary_sample_assets?.count ?? 0} Cloudinary stock samples</p>
                                    {lr.flagged?.length > 0 && (
                                      <ul className="font-mono mt-1 max-h-32 overflow-auto bg-white/60 rounded p-2" data-testid="cloudinary-exposure-flagged-list">
                                        {lr.flagged.map((f) => <li key={f.public_id}>[{f.severity}] {f.public_id} · {f.resource_type} · {f.bytes} B · {String(f.created_at).slice(0, 10)}</li>)}
                                      </ul>
                                    )}
                                    {lr.listing_errors?.length > 0 && <p className="text-red-700">Listing errors: {lr.listing_errors.join('; ')}</p>}
                                  </div>
                                ) : <p className="text-xs mt-1">No audit recorded in this database yet.</p>}
                                <p className="text-[11px] text-muted-foreground mt-1">Allowed public: {Object.entries(ce.expected_public || {}).map(([k, v]) => `${k} (${v})`).join(' · ')}</p>
                                <p className="text-[11px] text-muted-foreground mt-1">Compares: {ce.compares}</p>
                                <div className="mt-3 border-t pt-2">
                                  <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <p className="font-semibold text-xs">Approved preview-artefact deletions (production cloud only, literal list)</p>
                                    <div className="flex gap-2">
                                      <Button onClick={() => runArtefactDeletion(true)} disabled={artefactRunning} variant="outline" size="sm" className="rounded-full" data-testid="artefact-dry-run-btn">Dry-run</Button>
                                      <Button onClick={() => runArtefactDeletion(false)} disabled={artefactRunning || !artefactPlan || artefactPlan.problems?.length > 0 || artefactPlan.live_blockers?.length > 0} variant="destructive" size="sm" className="rounded-full" data-testid="artefact-live-run-btn">Delete approved list</Button>
                                    </div>
                                  </div>
                                  {artefactPlan && (() => {
                                    const dbLine = (t) => t ? ` · this_db users ${t.users} / purge_reports ${t.purge_reports} / scrub_log ${t.purge_scrub_log} / merged ${t.merged_users_archive} / biometrics ${t.biometrics}` : ' · this_db n/a (no uid)';
                                    const factLine = (r) => `${r.public_id} · ${r.type} · ${r.bytes} B · created ${String(r.created_at).slice(0, 10)} · last written ${String(r.last_written_at).slice(0, 10)}`;
                                    return (
                                    <div className="text-xs mt-2 space-y-1" data-testid="artefact-plan">
                                      <p>{artefactPlan.mode} · cloud fp {artefactPlan.cloud_fingerprint} · plan {artefactPlan.plan?.length} · problems {artefactPlan.problems?.length} · live blockers {artefactPlan.live_blockers?.length || 0} · already deleted {artefactPlan.already_deleted?.length || 0} · deleted {artefactPlan.deleted?.length}{artefactPlan.not_deleted_reason ? ` · ${artefactPlan.not_deleted_reason}` : ''}</p>
                                      <ul className="font-mono max-h-48 overflow-auto bg-white/60 rounded p-2" data-testid="artefact-plan-list">
                                        {artefactPlan.plan?.map((p) => <li key={p.public_id} className={p.this_db?.users > 0 ? 'text-red-700 font-bold' : ''}>{factLine(p)} · {p.why}{dbLine(p.this_db)}{p.this_db?.users > 0 ? ' · LIVE ACCOUNT - BLOCKS DELETION' : ''}</li>)}
                                        {artefactPlan.problems?.map((p, i) => <li key={`prob-${i}`} className="text-red-700">PROBLEM: {JSON.stringify(p.item)} → {p.reason} ({p.resolved?.length} resolved){dbLine(p.this_db)}</li>)}
                                      </ul>
                                      {artefactPlan.already_deleted?.length > 0 && (<>
                                        <p className="font-semibold">Already deleted (recorded in cloudinary_deletions; not counted as problems):</p>
                                        <ul className="font-mono max-h-32 overflow-auto bg-white/60 rounded p-2" data-testid="artefact-already-deleted-list">
                                          {artefactPlan.already_deleted.map((h, i) => <li key={`ad-${i}`}>{h.public_id} · deleted {String(h.deleted_at).slice(0, 19)}Z{dbLine(h.this_db)}</li>)}
                                        </ul>
                                      </>)}
                                      <p className="font-semibold">Held for facts (not on the list):</p>
                                      <ul className="font-mono max-h-32 overflow-auto bg-white/60 rounded p-2" data-testid="artefact-held-list">
                                        {artefactPlan.held_for_facts?.map((h, i) => <li key={`held-${i}`}>{h.item.uid_prefix}/{h.item.kind}: {h.resolved?.length ? h.resolved.map(factLine).join('; ') : 'not found'}{dbLine(h.this_db)} · {h.item.why}</li>)}
                                      </ul>
                                      <p className="font-semibold">Reserved — never on the list:</p>
                                      <ul className="font-mono max-h-32 overflow-auto bg-white/60 rounded p-2" data-testid="artefact-reserved-list">
                                        {artefactPlan.reserved_not_for_deletion?.map((h, i) => <li key={`res-${i}`}>{h.item.uid_prefix}/{h.item.kind}: {h.resolved?.length ? h.resolved.map(factLine).join('; ') : 'not found'}{dbLine(h.this_db)} · {h.item.why}</li>)}
                                      </ul>
                                      <p className="font-semibold">Proposed — not approved, never deleted by this control:</p>
                                      <ul className="font-mono max-h-32 overflow-auto bg-white/60 rounded p-2" data-testid="artefact-proposed-list">
                                        {artefactPlan.proposed_not_approved?.map((h, i) => <li key={`prop-${i}`}>{h.item.uid_prefix}/{h.item.kind}: {h.resolved?.length ? h.resolved.map(factLine).join('; ') : 'not found'}{dbLine(h.this_db)} · {h.item.why}</li>)}
                                      </ul>
                                      {artefactPlan.deleted?.length > 0 && <ul className="font-mono bg-white/60 rounded p-2" data-testid="artefact-deleted-list">{artefactPlan.deleted.map((d) => <li key={d.id}>DELETED {d.public_id} → {String(d.cloudinary_result)} · {String(d.deleted_at).slice(0, 19)}Z · url {d.versioned_url}</li>)}</ul>}
                                    </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            );
                          })()}
                          {sc.country_gate && (
                            <div className={`rounded-lg border p-3 self-start ${sc.country_gate.enabled ? 'border-sky-300 bg-sky-50' : 'border-border bg-muted/30'}`} data-testid="startup-check-country-gate">
                              <p className="font-semibold">Country gate (account creation only): <span data-testid="country-gate-status">{sc.country_gate.enabled ? 'ON' : 'OFF'}</span> · allowed {sc.country_gate.allowed_countries.join(', ')} · invite-only {sc.country_gate.invite_only ? 'ON' : 'off'}</p>
                              <p className="text-xs text-muted-foreground mt-1">GEO_BLOCKING_ENABLED: {String(sc.country_gate.GEO_BLOCKING_ENABLED)} · ALLOWED_COUNTRIES: {String(sc.country_gate.ALLOWED_COUNTRIES)} · INVITE_ONLY: {String(sc.country_gate.INVITE_ONLY)}</p>
                              <p className="text-xs text-muted-foreground">Country source for this request: {sc.country_gate.country_source.this_request_source} ({sc.country_gate.country_source.this_request_country || 'unknown'}) · CF-IPCountry header present: {String(sc.country_gate.country_source.cf_ipcountry_header_on_this_request)} · forwarded hops {sc.country_gate.country_source.forwarded_hops_on_this_request} (trusted {sc.country_gate.country_source.trusted_hops}) · local DB {sc.country_gate.country_source.local_db.present ? `${sc.country_gate.country_source.local_db.database_type} ${sc.country_gate.country_source.local_db.build_date}` : 'MISSING'} · waitlist {sc.country_gate.waitlist_total} · active invites {sc.country_gate.invites_active}</p>
                              <p className="text-[11px] text-muted-foreground mt-1">{sc.country_gate.country_source.local_db.attribution || ''} · {sc.country_gate.checked_at}</p>
                            </div>
                          )}
                          {sc.photo_retention && (
                            <div className={`rounded-lg border p-3 self-start ${sc.photo_retention.deletion_paused ? "border-amber-300 bg-amber-50" : "border-rose-200 bg-rose-50"}`} data-testid="startup-check-photo-retention">
                              <p className="font-semibold">Photo retention: <span data-testid="photo-retention-mode">{sc.photo_retention.deletion_paused ? 'PRESERVED — legal hold (nothing is deleted; the nightly run only reports what would have been eligible)' : 'DELETING — RETENTION_DELETION_RESUME=true'}</span></p>
                              <p className="text-xs text-muted-foreground mt-1">RETENTION_DELETION_RESUME: {String(sc.photo_retention.RETENTION_DELETION_RESUME)}
                                {sc.photo_retention.last_run && <> · last run {formatAwst(sc.photo_retention.last_run.started_at)}: {sc.photo_retention.last_run.summary?.mode || sc.photo_retention.last_run.status}, eligible {sc.photo_retention.last_run.summary?.eligible ?? '—'}, preserved {sc.photo_retention.last_run.summary?.preserved_count ?? 0}, deleted {sc.photo_retention.last_run.summary?.deleted ?? 0}</>}
                              </p>
                            </div>
                          )}
                          <div className={`rounded-lg border p-3 ${ttlTone}`} data-testid="startup-check-backup-ttl">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <p className="font-semibold">Backup-TTL mode: <span data-testid="backup-ttl-effective-mode">{ttl.effective_mode}</span></p>
                              <Button onClick={runTtlDryRun} disabled={ttlRunning} variant="outline" size="sm" className="rounded-full" data-testid="run-ttl-dry-run-btn">
                                <RefreshCw className={`w-4 h-4 mr-2 ${ttlRunning ? 'animate-spin' : ''}`} />
                                {ttlRunning ? 'Scanning...' : 'Run dry-run now'}
                              </Button>
                            </div>
                            <p className="font-mono text-xs">BACKUP_TTL_ENABLED={ttl.BACKUP_TTL_ENABLED} · BACKUP_TTL_DRY_RUN={ttl.BACKUP_TTL_DRY_RUN}</p>
                            <p className="font-mono text-xs break-all">BACKUP_TTL_EXPECTED_DB_NAME={ttl.BACKUP_TTL_EXPECTED_DB_NAME} · DB_NAME={ttl.DB_NAME} · assertion {ttl.db_assertion_ok ? 'OK' : 'FAILS'}</p>
                            <p className={`text-xs mt-1 font-semibold ${ttl.live_gate?.startsWith('BLOCKED') ? 'text-red-700' : 'text-muted-foreground'}`} data-testid="backup-ttl-live-gate">
                              LIVE gate ({ttl.reviewed_dry_run_max_age_days}-day reviewed dry-run): {ttl.live_gate}
                              {ttl.reviewed_dry_run ? ` · last reviewed ${String(ttl.reviewed_dry_run.ran_at).slice(0, 16)} UTC by ${String(ttl.reviewed_dry_run.trigger).replace('manual-dry-run:', '')} (${ttl.reviewed_dry_run.orphan_count} would-delete)` : ' · no reviewed dry-run on record'}
                            </p>
                            {shownRun ? (
                              <div className="mt-2 text-xs" data-testid="backup-ttl-run-result">
                                <p className="font-semibold">
                                  {ttlDryRun ? 'Manual dry-run' : 'Last recorded run'} · {shownRun.trigger} · {String(shownRun.ran_at).slice(0, 19)} UTC · mode={shownRun.mode}
                                </p>
                                {shownRun.aborted_reason && <p className="text-red-700">Aborted: {shownRun.aborted_reason}</p>}
                                {shownRun.error && <p className="text-red-700">Error: {shownRun.error}</p>}
                                <p>scanned {shownRun.scanned} · kept {shownRun.kept} · <span data-testid="backup-ttl-orphan-count">{shownRun.orphan_count}</span> would-delete · {shownRun.removed_count} actually removed</p>
                                {shownRun.orphans?.length > 0 ? (
                                  <ul className="font-mono mt-1 max-h-40 overflow-auto bg-white/60 rounded p-2" data-testid="backup-ttl-would-delete-list">
                                    {shownRun.orphans.map((o) => <li key={o.public_id}>{o.public_id}{o.bytes ? ` (${o.bytes} B)` : ''}</li>)}
                                  </ul>
                                ) : !shownRun.aborted_reason && !shownRun.error && <p className="text-green-800">Would-delete list is empty.</p>}
                                <div className="mt-2 flex items-start justify-between gap-2 flex-wrap">
                                  <p className="font-semibold" data-testid="backup-ttl-expired-summary">
                                    Expired backups of existing users (last written &gt; {shownRun.expired_max_age_days ?? ttl.BACKUP_MAX_AGE_DAYS}d ago): <span data-testid="backup-ttl-expired-count">{shownRun.expired_count ?? 0}</span> — detect &amp; report only, never deleted by the job
                                  </p>
                                  {shownRun.mode === 'live' && (
                                    <p className="text-xs" data-testid="backup-ttl-expired-live-result">
                                      Live expiry: {shownRun.expired_removed_count ?? 0} deleted and probed exists:false · survivors: <span className={(shownRun.expired_survivors || []).length ? 'text-red-700 font-semibold' : ''}>{(shownRun.expired_survivors || []).length}</span> (evidence rows in backup_ttl_deletions)
                                    </p>
                                  )}
                                  {shownRun.mode === 'dry_run' && <p className="text-xs text-muted-foreground">On a live run these {shownRun.expired_count ?? 0} would be deleted (holds exempt; exports/ and clinicians/ never listed).</p>}
                                  {shownRun.refresh_gate && (
                                    <p className="text-xs" data-testid="backup-ttl-refresh-gate">
                                      Active accounts' files: <span className={shownRun.refresh_gate.ok ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}>{shownRun.refresh_gate.ok ? 'refresh gate OK' : 'refresh gate BLOCKING'}</span>
                                      {' · '}last weekly refresh: {shownRun.refresh_gate.last_refresh_finished_at ? formatAwst(shownRun.refresh_gate.last_refresh_finished_at) : 'none in the last ' + shownRun.refresh_gate.max_age_days + ' days'}
                                      {' · '}<span data-testid="backup-ttl-active-blocked-count">{shownRun.expired_active_blocked_count ?? 0}</span> old file(s) of active accounts reported, never deleted — {shownRun.refresh_gate.note}
                                    </p>
                                  )}
                                  {ttlDryRun && ttlDryRun.mode === 'dry_run' && String(ttlDryRun.trigger).startsWith('manual-dry-run:') && (ttlDryRun.expired_count > 0 || ttlDryRun.legacy_orphan_count > 0) && (
                                    <Button onClick={() => runExpiredPurge(ttlDryRun.expired_count + (ttlDryRun.legacy_orphan_count || 0))} disabled={ttlRunning} variant="destructive" size="sm" className="rounded-full" data-testid="purge-expired-backups-btn">
                                      Purge {ttlDryRun.expired_count + (ttlDryRun.legacy_orphan_count || 0)} expired/legacy now
                                    </Button>
                                  )}
                                </div>
                                {shownRun.expired?.length > 0 && (
                                  <ul className="font-mono mt-1 max-h-40 overflow-auto bg-white/60 rounded p-2" data-testid="backup-ttl-expired-list">
                                    {shownRun.expired.map((e) => <li key={e.public_id}>{e.public_id} · last written {String(e.last_written_at).slice(0, 10)} ({e.age_days}d)</li>)}
                                  </ul>
                                )}
                                <p className="font-semibold mt-2" data-testid="backup-ttl-legacy-summary">
                                  Legacy-prefix orphans (clinicians/, feedback/ — document gone from its collection): <span data-testid="backup-ttl-legacy-count">{shownRun.legacy_orphan_count ?? 0}</span> — detect &amp; report only
                                </p>
                                {shownRun.legacy_orphans?.length > 0 && (
                                  <ul className="font-mono mt-1 max-h-40 overflow-auto bg-white/60 rounded p-2" data-testid="backup-ttl-legacy-list">
                                    {shownRun.legacy_orphans.map((e) => <li key={e.public_id}>{e.public_id} · {e.collection} · {e.age_days}d</li>)}
                                  </ul>
                                )}
                                {shownRun.mode === 'expired_purge' && (
                                  <p data-testid="backup-ttl-purge-result">Purge: {shownRun.removed?.length || 0} expired + {shownRun.legacy_removed?.length || 0} legacy removed · {shownRun.skipped?.length || 0} skipped{shownRun.skipped?.length ? ` (${shownRun.skipped.map((s) => s.reason).join(', ')})` : ''}</p>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground mt-2" data-testid="backup-ttl-run-result">No run recorded yet. The manual dry-run is read-only and can never delete.</p>
                            )}
                            <p className="text-[11px] text-muted-foreground mt-1">Compares: {ttl.compares}</p>
                          </div>
                        </div>
                        {stuckReport && (
                          <div className="mt-5" data-testid="stuck-entries-panel">
                            <h4 className="font-bold mb-1">Stuck Entry Sweep — detect &amp; report ({stuckReport.total} unfinalized over 48h)</h4>
                            <p className="text-[11px] text-muted-foreground mb-2">Compares: {stuckReport.compares}</p>
                            {stuckReport.total === 0 && <p className="text-sm text-green-800" data-testid="stuck-none">Nothing stuck. No action available.</p>}
                            {Object.entries(stuckReport.buckets).map(([bucket, b]) => (
                              <div key={bucket} className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-2" data-testid={`stuck-bucket-${bucket}`}>
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                  <div>
                                    <p className="font-semibold font-mono text-sm">{bucket} · {b.entries.length}</p>
                                    <p className="text-xs">{b.why}</p>
                                  </div>
                                  <div className="flex gap-1 flex-wrap">
                                    {b.allowed_actions.map((a) => (
                                      <Button key={a} size="sm" variant={a === 'delete' ? 'destructive' : 'outline'} className="rounded-full h-7 text-xs" disabled={!!stuckBusy}
                                        onClick={() => resolveStuckBucket(bucket, a, b.entries)} data-testid={`stuck-action-${bucket}-${a}`}>
                                        {stuckBusy === `${bucket}:${a}` ? 'Working…' : `${a} all ${b.entries.length}`}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                                <ul className="text-xs mt-2 space-y-1 max-h-48 overflow-auto bg-white/60 rounded p-2">
                                  {b.entries.map((e) => (
                                    <li key={e.entry_id} className="font-mono">
                                      {e.entry_id.slice(0, 8)} · {e.user_email} · {String(e.created_at).slice(0, 10)} · {e.meal_type || '—'} · is_finalized={String(e.is_finalized_field)} · image={e.has_image ? 'yes' : 'no'}{e.photo_deleted ? ' (photo deleted)' : ''} · analysis={e.analysis_preview === null || e.analysis_preview === undefined ? 'null' : JSON.stringify(e.analysis_preview)}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                            <p className="text-[11px] text-muted-foreground">Actions: finalize — {stuckReport.actions.finalize}. reanalyze — {stuckReport.actions.reanalyze}. delete — {stuckReport.actions.delete}.</p>
                            {stuckResult && (
                              <pre className="text-[11px] mt-2 bg-background/70 rounded p-2 max-h-56 overflow-auto" data-testid="stuck-result">{JSON.stringify(stuckResult, null, 1)}</pre>
                            )}
                          </div>
                        )}
                        {jobLedger && (
                          <div className="mt-5" data-testid="job-ledger-panel">
                            <h4 className="font-bold mb-1">Job Run Ledger — registered vs proven</h4>
                            <p className="text-[11px] text-muted-foreground mb-2">Compares: {jobLedger.compares}</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead><tr className="text-left border-b border-border/60"><th className="py-1 pr-2">Job</th><th className="py-1 pr-2">Next run (scheduler)</th><th className="py-1 pr-2">Last completed (ledger)</th><th className="py-1 pr-2">OK / err</th><th className="py-1 pr-2">Last summary</th><th className="py-1"></th></tr></thead>
                                <tbody>
                                  {jobLedger.jobs.map((j) => (
                                    <tr key={j.job_id} className="border-b border-border/30 align-top" data-testid={`job-row-${j.job_id}`}>
                                      <td className="py-1 pr-2 font-mono">{j.job_id}<br /><span className="text-muted-foreground">{j.function}</span></td>
                                      <td className="py-1 pr-2 font-mono">{j.next_run_time ? String(j.next_run_time).slice(0, 16) : '—'}</td>
                                      <td className="py-1 pr-2 font-mono" data-testid={`job-last-run-${j.job_id}`}>
                                        {j.last_run ? <>{String(j.last_run.started_at).slice(0, 16)}<br /><span className={j.last_run.status === 'completed' ? 'text-green-700' : 'text-red-700'}>{j.last_run.status}</span> · {j.last_run.duration_ms}ms<br /><span className="text-muted-foreground">{j.last_run.invoked_by}</span></> : <span className="text-red-700 font-semibold">NEVER RECORDED</span>}
                                      </td>
                                      <td className="py-1 pr-2 font-mono">{j.completed_runs_recorded} / {j.runs_with_error_recorded}</td>
                                      <td className="py-1 pr-2 font-mono break-all max-w-[360px]">{j.last_run?.summary ? JSON.stringify(j.last_run.summary).slice(0, 220) : '—'}{j.last_run?.error ? <span className="text-red-700"> {j.last_run.error}</span> : null}</td>
                                      <td className="py-1"><Button size="sm" variant="outline" className="rounded-full h-7 text-xs" disabled={runNowBusy === j.job_id} onClick={() => runJobNow(j.job_id)} data-testid={`run-now-${j.job_id}`}>Run now</Button></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </>);
                  })()}
                  {diagData ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="rounded-xl bg-teal-50 p-4"><p className="text-xs uppercase text-teal-700 font-bold">Users</p><p className="text-2xl font-bold" data-testid="diag-users-total">{diagData.database.users_total}</p></div>
                        <div className="rounded-xl bg-teal-50 p-4"><p className="text-xs uppercase text-teal-700 font-bold">Food Entries</p><p className="text-2xl font-bold">{diagData.database.food_entries_total}</p></div>
                        <div className="rounded-xl bg-red-50 p-4"><p className="text-xs uppercase text-red-700 font-bold">Corrupted Hashes</p><p className="text-2xl font-bold" data-testid="diag-corrupted-count">{diagData.integrity.corrupted_hash_count}</p></div>
                        <div className="rounded-xl bg-amber-50 p-4"><p className="text-xs uppercase text-amber-700 font-bold">Duplicate Emails</p><p className="text-2xl font-bold">{diagData.integrity.duplicate_email_groups.length}</p></div>
                        <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs uppercase text-slate-600 font-bold">No Consent</p><p className="text-2xl font-bold">{diagData.integrity.non_consented_users}</p></div>
                        <div className="rounded-xl bg-orange-50 p-4"><p className="text-xs uppercase text-orange-700 font-bold">Pre-release Consents</p><p className="text-2xl font-bold" data-testid="diag-prerelease-consents">{diagData.integrity.pre_release_consents_pending ?? '—'}</p><p className="text-[10px] text-orange-700/70">pending re-consent at publication</p></div>
                        <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs uppercase text-slate-600 font-bold">Photos &gt;7d Pending</p><p className="text-2xl font-bold">{diagData.retention.photos_over_7_days_not_deleted}</p></div>
                        <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs uppercase text-slate-600 font-bold">Unfinalized &gt;48h</p><p className="text-2xl font-bold">{diagData.auto_finalization.unfinalized_entries_over_48h}</p></div>
                        <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs uppercase text-slate-600 font-bold">Newest Signup</p><p className="text-sm font-semibold break-all">{diagData.database.newest_user?.email}</p><p className="text-xs text-muted-foreground">{diagData.database.newest_user?.created_at?.slice(0, 19)}</p></div>
                      </div>
                      <p className="text-sm font-bold mb-2 text-muted-foreground">Raw output (copy for evidence):</p>
                      <pre className="bg-slate-900 text-emerald-300 text-xs rounded-xl p-4 overflow-auto max-h-96" data-testid="diagnostics-raw-json">{JSON.stringify(diagData, null, 2)}</pre>
                    </>
                  ) : (
                    <p className="text-muted-foreground">{diagLoading ? 'Running diagnostics...' : 'Click Re-run to fetch.'}</p>
                  )}
                </div>
              )}
              {view === 'indexes' && (
                <div className="bg-card rounded-3xl border border-border/50 shadow-lg p-8 mb-8" data-testid="indexes-panel">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                    <h2 className="text-2xl font-bold">Database Indexes (read-only)</h2>
                    <div className="flex items-center gap-3">
                      <select
                        data-testid="index-collection-picker"
                        value={selectedCollection}
                        onChange={(e) => { setSelectedCollection(e.target.value); fetchIndexesFor(e.target.value); }}
                        className="border-2 border-border rounded-full px-4 py-2 bg-background font-semibold text-sm"
                      >
                        {dbCollections.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <Button onClick={() => fetchIndexesFor(selectedCollection)} disabled={loadingIndexes} className="rounded-full" data-testid="indexes-refresh-btn">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loadingIndexes ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>
                  {loadingIndexes ? (
                    <p className="text-muted-foreground">Loading indexes...</p>
                  ) : collectionIndexes ? (
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="indexes-table">
                        <thead className="bg-muted/50 border-b border-border">
                          <tr>
                            <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Index Name</th>
                            <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Keys</th>
                            <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Unique</th>
                            <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Partial Filter</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(collectionIndexes).map(([name, spec]) => (
                            <tr key={name} className="border-b border-border/50" data-testid={`index-row-${name}`}>
                              <td className="px-6 py-4 font-mono text-sm font-semibold">{name}</td>
                              <td className="px-6 py-4 font-mono text-sm">{(spec.key || []).map(([f, d]) => `${f}: ${d}`).join(', ')}</td>
                              <td className="px-6 py-4">
                                {spec.unique ? (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">unique: true</span>
                                ) : (
                                  <span className="text-muted-foreground text-sm">—</span>
                                )}
                              </td>
                              <td className="px-6 py-4 font-mono text-xs">
                                {spec.partialFilterExpression ? JSON.stringify(spec.partialFilterExpression) : <span className="text-muted-foreground">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Select a collection to view its indexes.</p>
                  )}
                </div>
              )}
              {view === 'users' && <Q7RemovalCard token={adminToken} onDone={() => { fetchData(); fetchSoftDeletedUsers(); setDeletedPanelKey((k) => k + 1); }} />}
              {view === 'users' && <CountryGateCard token={adminToken} />}
              {view === 'users' && <DeletedAccountsPanel key={deletedPanelKey} token={adminToken} />}
              {view === 'users' && (
                <div className="bg-card rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-border/50 flex items-center gap-3">
                    <Input
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by email or account id prefix (e.g. 7a315057)"
                      className="max-w-md"
                      data-testid="users-search-input"
                    />
                    <span className="text-xs text-muted-foreground" data-testid="users-search-count">{visibleUsers.length} of {users.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Id</th>
                          <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Email</th>
                          <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Role</th>
                          <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Joined</th>
                          <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Consent</th>
                          <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Meals</th>
                          <th className="text-left px-6 py-4 text-sm font-bold uppercase tracking-widest text-muted-foreground">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleUsers.map((userItem, index) => {
                          const userEntryCount = entries.filter(e => e.user_id === userItem.id).length;
                          return (
                            <tr 
                              key={userItem.id} 
                              className="border-b border-border/50 hover:bg-muted/20 cursor-pointer" 
                              data-testid={`user-row-${index}`}
                              onClick={() => handleUserClick(userItem)}
                            >
                              <td className="px-6 py-4 font-mono text-sm" title={userItem.id} data-testid={`user-id-prefix-${index}`}>{(userItem.id || '').slice(0, 8)}</td>
                              <td className="px-6 py-4">{userItem.email}</td>
                              <td className="px-6 py-4">
                                {userItem.is_admin ? (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-secondary/20 text-secondary">Admin</span>
                                ) : (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary/20 text-primary">User</span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(userItem.created_at)}</td>
                              <td className="px-6 py-4" data-testid={`consent-status-${index}`}>
                                {userItem.privacy_policy_consent ? (
                                  <div>
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                      v{userItem.privacy_policy_consent.version}
                                    </span>
                                    <p className="text-xs text-muted-foreground mt-1">{formatDate(userItem.privacy_policy_consent.accepted_at)}</p>
                                  </div>
                                ) : (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">No consent</span>
                                )}
                              </td>
                              <td className="px-6 py-4 font-semibold">{userEntryCount}</td>
                              <td className="px-6 py-4">
                                <div className="flex gap-2">
                                  <Button
                                    data-testid={`view-user-${index}`}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full"
                                    onClick={(e) => { e.stopPropagation(); handleUserClick(userItem); }}
                                  >
                                    View Details
                                  </Button>
                                  {!userItem.is_admin && (
                                  <Button
                                    data-testid={`delete-user-${index}`}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full text-red-500 border-red-300 hover:bg-red-50"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteUser(userItem.id, userItem.email); }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {view === 'duplicates' && (
                <div className="bg-card rounded-2xl border border-border/50 shadow-lg overflow-hidden">
                  <div className="p-6 border-b border-border/50 bg-gradient-to-r from-indigo-500/10 to-violet-500/10">
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      <GitMerge className="w-6 h-6 text-indigo-500" />
                      Duplicate User Accounts
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Merge accounts with identical email addresses to consolidate user data
                    </p>
                  </div>

                  {loadingDuplicates ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-indigo-500" />
                      <p className="mt-2 text-muted-foreground">Scanning for duplicates...</p>
                    </div>
                  ) : duplicateUsers.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Check className="w-12 h-12 mx-auto mb-3 text-green-500" />
                      <p className="text-lg font-semibold">No duplicate accounts found</p>
                      <p className="text-sm mt-1">All user accounts have unique email addresses.</p>
                      <div className="mt-6 max-w-md mx-auto">
                        <Button
                          data-testid="create-unique-email-index-btn"
                          onClick={handleCreateUniqueEmailIndex}
                          disabled={creatingEmailIndex}
                          className="rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white"
                        >
                          {creatingEmailIndex ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                          Create Unique Email Index
                        </Button>
                        <p className="text-xs text-muted-foreground mt-2">
                          Structurally prevents duplicate emails forever. Refuses (with a list) if any duplicates still exist.
                        </p>
                        {emailIndexResult && (
                          <pre data-testid="email-index-result" className="mt-3 bg-slate-900 text-emerald-300 text-xs rounded-xl p-3 overflow-auto text-left">{JSON.stringify(emailIndexResult, null, 2)}</pre>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="divide-y divide-border/50">
                      {duplicateUsers.map((group, groupIndex) => (
                        <div key={group.email} className="p-6">
                          <div className="flex items-center gap-2 mb-4">
                            <AlertCircle className="w-5 h-5 text-amber-500" />
                            <span className="font-semibold text-lg">{group.email}</span>
                            <span className="text-sm text-muted-foreground">
                              ({group.users.length} accounts)
                            </span>
                          </div>

                          <div className="grid gap-4 md:grid-cols-2">
                            {group.users.map((userItem, userIndex) => (
                              <div
                                key={userItem.id}
                                className={`p-4 rounded-xl border-2 ${
                                  userIndex === 0
                                    ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20'
                                    : 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                    userIndex === 0
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {userIndex === 0 ? 'Primary Account' : 'Duplicate'}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    ID: {userItem.id.slice(0, 8)}...
                                  </span>
                                </div>

                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Email:</span>
                                    <span className="font-medium">{userItem.email}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Created:</span>
                                    <span>{formatDate(userItem.created_at)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Food Entries:</span>
                                    <span className="font-semibold">{userItem.entry_count || 0}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Role:</span>
                                    <span className={`font-medium ${
                                      userItem.is_admin ? 'text-secondary' : 'text-primary'
                                    }`}>
                                      {userItem.is_admin ? 'Admin' : 'User'}
                                    </span>
                                  </div>
                                </div>

                                {userIndex > 0 && (
                                  <Button
                                    className="w-full mt-4 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
                                    onClick={() => handleMergeUsers(
                                      group.users[0].id,
                                      userItem.id,
                                      group.users[0].email,
                                      userItem.email
                                    )}
                                    disabled={mergingUsers}
                                  >
                                    {mergingUsers ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Merging...
                                      </>
                                    ) : (
                                      <>
                                        <GitMerge className="w-4 h-4 mr-2" />
                                        Merge into Primary
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {view === 'claims' && (
                <div className="space-y-4">
                  {loadingClaims ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                      <p className="text-muted-foreground mt-2">Loading claims...</p>
                    </div>
                  ) : claimRequests.length === 0 ? (
                    <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
                      <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No claim requests yet</p>
                    </div>
                  ) : (
                    claimRequests.map((claim, index) => (
                      <motion.div
                        key={claim.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`bg-card rounded-2xl border p-6 ${
                          claim.status === 'pending' ? 'border-amber-300 bg-amber-50/30' :
                          claim.status === 'approved' ? 'border-green-300 bg-green-50/30' :
                          'border-red-300 bg-red-50/30'
                        }`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                claim.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                claim.status === 'approved' ? 'bg-green-100 text-green-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {claim.status.toUpperCase()}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                Submitted {formatDate(claim.created_at)}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-1">Claiming Email</p>
                                <p className="font-medium">{claim.email}</p>
                              </div>
                              <div>
                                <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-1">Approximate Signup</p>
                                <p>{claim.approximate_signup_date || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-1">Account to Claim</p>
                                <p className="text-sm">{claim.unclaimed_account?.email || 'Unknown'}</p>
                              </div>
                              <div>
                                <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-1">Meals in Account</p>
                                <p className="font-semibold">{claim.entry_count}</p>
                              </div>
                            </div>
                            
                            {claim.message && (
                              <div className="mb-4">
                                <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-1">Message</p>
                                <p className="text-sm bg-muted/50 p-3 rounded-lg">{claim.message}</p>
                              </div>
                            )}
                            
                            {claim.sample_public_ids && claim.sample_public_ids.length > 0 && (
                              <div>
                                <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-2">Sample photos (ids - photos are viewed only through the audited entry view)</p>
                                <ul className="text-xs font-mono" data-testid="claim-sample-public-ids">{claim.sample_public_ids.map((pid) => <li key={pid}>{pid}</li>)}</ul>
                              </div>
                            )}
                          </div>
                          
                          {claim.status === 'pending' && (
                            <div className="flex gap-2 lg:flex-col">
                              <Button
                                data-testid={`approve-claim-${index}`}
                                onClick={() => handleApproveClaim(claim.id)}
                                disabled={processingClaim === claim.id}
                                className="rounded-full bg-green-500 hover:bg-green-600"
                              >
                                {processingClaim === claim.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-4 h-4 mr-2" />
                                    Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                data-testid={`reject-claim-${index}`}
                                onClick={() => handleRejectClaim(claim.id)}
                                disabled={processingClaim === claim.id}
                                variant="outline"
                                className="rounded-full border-red-500 text-red-500 hover:bg-red-50"
                              >
                                <X className="w-4 h-4 mr-2" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {view === 'integrity' && (
                <div className="space-y-6">
                  {/* Header with Run Check button */}
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-xl font-bold">Data Integrity Monitor</h2>
                      <p className="text-sm text-muted-foreground">
                        Automatic checks run daily at 00:00 WST. Last check compares MongoDB with Cloudinary backups.
                      </p>
                    </div>
                    <Button
                      data-testid="run-integrity-check"
                      onClick={handleRunIntegrityCheck}
                      disabled={runningCheck}
                      className="rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                    >
                      {runningCheck ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running Check...
                        </>
                      ) : (
                        <>
                          <Activity className="w-4 h-4 mr-2" />
                          Run Check Now
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {loadingDiscrepancies ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                      <p className="text-muted-foreground mt-2">Loading discrepancies...</p>
                    </div>
                  ) : discrepancies.length === 0 ? (
                    <div className="text-center py-12 bg-green-50 rounded-2xl border border-green-200">
                      <Check className="w-12 h-12 mx-auto text-green-500 mb-4" />
                      <h3 className="font-semibold text-green-800">All Data in Sync</h3>
                      <p className="text-green-700 text-sm">No discrepancies found between database and Cloudinary backups.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {discrepancies.map((disc, index) => (
                        <motion.div
                          key={disc.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`bg-card rounded-2xl border p-6 ${
                            disc.status === 'pending' ? 'border-blue-300 bg-blue-50/30' :
                            disc.status === 'approved' ? 'border-green-300 bg-green-50/30' :
                            'border-gray-300 bg-gray-50/30'
                          }`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  disc.type === 'missing_user' ? 'bg-red-100 text-red-700' :
                                  disc.type === 'missing_entries' ? 'bg-amber-100 text-amber-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {disc.type === 'missing_user' ? 'MISSING USER' :
                                   disc.type === 'missing_entries' ? 'MISSING ENTRIES' :
                                   'DATA MISMATCH'}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  disc.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                                  disc.status === 'approved' ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {(disc.status || 'unknown').toUpperCase()}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  Detected {formatDate(disc.created_at)}
                                </span>
                              </div>
                              
                              <p className="mb-4 text-sm">{disc.details}</p>
                              
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div>
                                  <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-1">Cloudinary User ID</p>
                                  <p className="text-xs font-mono bg-muted p-2 rounded">{disc.cloudinary_user_id}</p>
                                </div>
                                {disc.cloudinary_email && (
                                  <div>
                                    <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-1">Backed Up Email</p>
                                    <p>{disc.cloudinary_email}</p>
                                  </div>
                                )}
                                <div>
                                  <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-1">Affected Items</p>
                                  <p className="font-semibold">{disc.entry_count} {disc.entry_count === 1 ? 'image' : 'images'}</p>
                                </div>
                              </div>
                              
                              {/* Sample images */}
                              {disc.sample_public_ids && disc.sample_public_ids.length > 0 && (
                                <div>
                                  <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-2">Sample photos (ids - photos are viewed only through the audited entry view)</p>
                                  <ul className="text-xs font-mono" data-testid="disc-sample-public-ids">{disc.sample_public_ids.map((pid) => <li key={pid}>{pid}</li>)}</ul>
                                </div>
                              )}
                            </div>
                            
                            {disc.status === 'pending' && (
                              <div className="flex gap-2 lg:flex-col">
                                <Button
                                  data-testid={`approve-recovery-${index}`}
                                  onClick={() => handleApproveRecovery(disc.id)}
                                  disabled={processingDiscrepancy === disc.id}
                                  className="rounded-full bg-green-500 hover:bg-green-600"
                                >
                                  {processingDiscrepancy === disc.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Check className="w-4 h-4 mr-2" />
                                      Recover
                                    </>
                                  )}
                                </Button>
                                <Button
                                  data-testid={`dismiss-discrepancy-${index}`}
                                  onClick={() => handleDismissDiscrepancy(disc.id)}
                                  disabled={processingDiscrepancy === disc.id}
                                  variant="outline"
                                  className="rounded-full"
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Dismiss
                                </Button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {view === 'backups' && (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-xl font-bold">Backup Management</h2>
                      <p className="text-sm text-muted-foreground">
                        Cloudinary stores backups of all users, biometrics, and food entries. Data auto-restores on redeployment if the database is empty.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        data-testid="refresh-backup-status"
                        onClick={fetchBackupStatus}
                        disabled={loadingBackupStatus}
                        variant="outline"
                        className="rounded-full"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loadingBackupStatus ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>

                  {loadingBackupStatus ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                      <p className="text-muted-foreground mt-2">Loading backup status...</p>
                    </div>
                  ) : backupStatus ? (
                    <div className="space-y-6">
                      {/* Status Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Database Status */}
                        <div className="bg-card rounded-2xl border border-border/50 p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <Database className="w-5 h-5 text-blue-600" />
                            </div>
                            <h3 className="font-bold text-lg">Database (MongoDB)</h3>
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Users</span>
                              <span className="font-bold text-lg">{backupStatus.database.users}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Food Entries</span>
                              <span className="font-bold text-lg">{backupStatus.database.food_entries}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Biometrics</span>
                              <span className="font-bold text-lg">{backupStatus.database.biometrics}</span>
                            </div>
                          </div>
                        </div>

                        {/* Cloudinary Backup Status */}
                        <div className="bg-card rounded-2xl border border-border/50 p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <Cloud className="w-5 h-5 text-purple-600" />
                            </div>
                            <h3 className="font-bold text-lg">Cloudinary Backups</h3>
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Users</span>
                              <span className={`font-bold text-lg ${backupStatus.sync_status.users_synced ? 'text-green-600' : 'text-amber-600'}`}>
                                {backupStatus.cloudinary_backups.users}
                                {backupStatus.sync_status.users_synced && <Check className="w-4 h-4 inline ml-1" />}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Food Entries (with metadata)</span>
                              <span className="font-bold text-lg">{backupStatus.cloudinary_backups.food_entries_with_metadata}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Biometrics</span>
                              <span className="font-bold text-lg">{backupStatus.cloudinary_backups.biometrics}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sync Status Alert */}
                      {backupStatus.sync_status.needs_backup ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                          <div className="flex items-start gap-4">
                            <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-amber-800 mb-1">Backup Sync Needed</h4>
                              <p className="text-amber-700 text-sm mb-4">
                                Some data in the database is not yet backed up to Cloudinary. Run a sync to ensure all data is protected.
                              </p>
                              <Button
                                data-testid="sync-to-cloudinary"
                                onClick={handleSyncToCloudinary}
                                disabled={syncing}
                                className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                              >
                                {syncing ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Syncing...
                                  </>
                                ) : (
                                  <>
                                    <Cloud className="w-4 h-4 mr-2" />
                                    Sync All to Cloudinary
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                          <div className="flex items-center gap-4">
                            <Check className="w-6 h-6 text-green-500" />
                            <div>
                              <h4 className="font-semibold text-green-800">All Data Backed Up</h4>
                              <p className="text-green-700 text-sm">Your data is safely stored in Cloudinary and will auto-restore on redeployment.</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Manual Actions */}
                      <div className="bg-card rounded-2xl border border-border/50 p-6">
                        <h3 className="font-bold text-lg mb-4">Manual Actions</h3>
                        <div className="flex flex-wrap gap-4">
                          <Button
                            data-testid="manual-sync"
                            onClick={handleSyncToCloudinary}
                            disabled={syncing}
                            variant="outline"
                            className="rounded-full border-purple-500 text-purple-600 hover:bg-purple-50"
                          >
                            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Cloud className="w-4 h-4 mr-2" />}
                            Sync All to Cloudinary
                          </Button>
                          <Button
                            data-testid="manual-restore"
                            onClick={handleRestoreFromCloudinary}
                            disabled={restoring}
                            variant="outline"
                            className="rounded-full border-blue-500 text-blue-600 hover:bg-blue-50"
                          >
                            {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                            Restore from Cloudinary
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3">
                          Note: Restore only adds missing records. Existing data is not overwritten.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Cloud className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Click refresh to load backup status</p>
                    </div>
                  )}
                  
                  {/* Soft-Deleted Users Section */}
                  <div className="bg-card rounded-2xl border border-border/50 p-6 mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-lg flex items-center gap-2">
                          <Trash2 className="w-5 h-5 text-amber-500" />
                          Pending deletion (restore or purge)
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Soft-deleted accounts still in the database. Restore them, or Purge to permanently destroy their database records AND all Cloudinary assets (photos + backups). The read-only history of deleted accounts is in Users → Deleted Users.
                        </p>
                        {lastPurgeReport && (
                          <div className="mt-4" data-testid="last-purge-report">
                            <p className="text-sm font-bold text-muted-foreground mb-1">Last purge report — {lastPurgeReport.user_id?.slice(0, 8)} · {lastPurgeReport.email} ({lastPurgeReport.at?.slice(0, 19)}):</p>
                            <pre className="bg-slate-900 text-emerald-300 text-xs rounded-xl p-4 overflow-auto max-h-64">{JSON.stringify(lastPurgeReport, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                      <Button
                        data-testid="refresh-deleted-users"
                        onClick={fetchSoftDeletedUsers}
                        disabled={loadingSoftDeleted}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loadingSoftDeleted ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                    
                    {loadingSoftDeleted ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                        <p className="text-muted-foreground mt-2 text-sm">Loading deleted users...</p>
                      </div>
                    ) : softDeletedUsers.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                        <p className="text-sm">No deleted users</p>
                        <p className="text-xs mt-1">All users are active</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {softDeletedUsers.map((deletedUser) => (
                          <div
                            key={deletedUser.id}
                            data-testid={`deleted-user-${deletedUser.id}`}
                            className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <Users className="w-5 h-5 text-amber-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{deletedUser.email}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {deletedUser.first_name && deletedUser.last_name && (
                                    <span>{deletedUser.first_name} {deletedUser.last_name}</span>
                                  )}
                                  {deletedUser.deleted_at && (() => {
                                    const days = Math.floor((Date.now() - new Date(deletedUser.deleted_at).getTime()) / 86400000);
                                    return (
                                      <span className="text-amber-600" data-testid={`deleted-user-age-${deletedUser.id}`}>
                                        Deleted {new Date(deletedUser.deleted_at).toLocaleDateString()} · {days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`}
                                        {deletedUser.merged_into
                                          ? ' · merge tombstone (retire deliberately)'
                                          : deletedUser.deletion_requested_by_user
                                            ? ` · SELF-REQUESTED — auto-purge in ${Math.max(0, 30 - days)} day(s)`
                                            : ' · admin soft-delete (sits indefinitely)'}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                data-testid={`restore-user-${deletedUser.id}`}
                                onClick={() => handleRestoreUser(deletedUser.id, deletedUser.email)}
                                disabled={restoringUser === deletedUser.id}
                                variant="outline"
                                size="sm"
                                className="rounded-full border-green-500 text-green-600 hover:bg-green-50"
                              >
                                {restoringUser === deletedUser.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <RotateCcw className="w-4 h-4 mr-1" />
                                    Restore
                                  </>
                                )}
                              </Button>
                              <Button
                                data-testid={`purge-user-${deletedUser.id}`}
                                onClick={() => initiatePurge(deletedUser)}
                                disabled={purgingUser === deletedUser.id}
                                variant="destructive"
                                size="sm"
                                className="rounded-full"
                              >
                                {purgingUser === deletedUser.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Purge
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Cloudinary Safety Net Section (Read-only) */}
                  <div className="bg-card rounded-2xl border border-border/50 p-6 mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-lg flex items-center gap-2">
                          <Cloud className="w-5 h-5 text-blue-500" />
                          Cloudinary Safety Net
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Immutable backups on Cloudinary. These cannot be deleted and serve as permanent recovery source.
                        </p>
                      </div>
                      <Button
                        data-testid="refresh-orphan-backups"
                        onClick={fetchOrphanBackups}
                        disabled={loadingOrphanBackups}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loadingOrphanBackups ? 'animate-spin' : ''}`} />
                        Check Status
                      </Button>
                    </div>
                    
                    {loadingOrphanBackups ? (
                      <div className="text-center py-6">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                        <p className="text-muted-foreground mt-2 text-sm">Checking Cloudinary...</p>
                      </div>
                    ) : orphanBackups.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground">
                        <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                        <p className="text-sm">All Cloudinary backups have matching database records</p>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <p className="text-sm text-blue-700 font-medium mb-2">
                          {orphanBackups.length} backup(s) exist without database records
                        </p>
                        <p className="text-xs text-blue-600">
                          Backups without matching database records (legacy). Note: the current Purge destroys a user's backups too — new orphans should no longer appear.
                        </p>
                        <div className="mt-3 space-y-2">
                          {orphanBackups.slice(0, 5).map((backup) => (
                            <div key={backup.id} className="text-xs bg-white/50 rounded px-3 py-2">
                              <span className="font-medium">{backup.email}</span>
                              <span className="text-muted-foreground ml-2">({backup.type})</span>
                            </div>
                          ))}
                          {orphanBackups.length > 5 && (
                            <p className="text-xs text-blue-600">...and {orphanBackups.length - 5} more</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Purge Confirmation Dialog */}
                  {purgeDialogOpen && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
                        <h3 className="text-lg font-bold text-amber-600 flex items-center gap-2 mb-4">
                          <AlertCircle className="w-5 h-5" />
                          Confirm Purge
                        </h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          You are about to permanently remove <strong>{userToPurge?.email}</strong> from the database <strong>and Cloudinary</strong>.
                        </p>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                          <p className="text-sm text-red-700 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            All Cloudinary assets (meal photos + account backups) will be permanently destroyed
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">
                          This deletes the user&apos;s records from all database collections AND destroys their Cloudinary photos and backup assets. This action is irreversible — nothing can be recovered afterwards.
                        </p>
                        <div className="flex gap-3 justify-end">
                          <Button
                            data-testid="cancel-purge"
                            variant="outline"
                            onClick={() => {
                              setPurgeDialogOpen(false);
                              setUserToPurge(null);
                            }}
                            className="rounded-full"
                          >
                            Cancel
                          </Button>
                          <Button
                            data-testid="confirm-purge"
                            variant="destructive"
                            onClick={confirmPurge}
                            disabled={purgingUser}
                            className="rounded-full"
                          >
                            {purgingUser ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Purge Permanently
                          </Button>
                        </div>
                        {purgingUser && purgeProgress && (
                          <p className="text-xs text-muted-foreground mt-2" data-testid="purge-progress">
                            Background job: step {purgeProgress.done}{purgeProgress.total ? `/${purgeProgress.total}` : ''} · {purgeProgress.note}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {view === 'login-help' && (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-xl font-bold">Login Help Requests</h2>
                      <p className="text-sm text-muted-foreground">
                        Users who reported difficulty logging in to their accounts
                      </p>
                    </div>
                    <Button
                      data-testid="refresh-login-help"
                      onClick={fetchLoginHelpRequests}
                      disabled={loadingLoginHelp}
                      variant="outline"
                      className="rounded-full"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingLoginHelp ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>

                  {loadingLoginHelp ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                      <p className="text-muted-foreground mt-2">Loading requests...</p>
                    </div>
                  ) : loginHelpRequests.length === 0 ? (
                    <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
                      <HelpCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No login help requests</p>
                      <p className="text-sm text-muted-foreground mt-1">When users report login issues, they&apos;ll appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {loginHelpRequests.map((request) => (
                        <div 
                          key={request.id}
                          className={`bg-card rounded-2xl border p-6 ${
                            request.resolved 
                              ? 'border-green-200 bg-green-50/30' 
                              : 'border-amber-200 bg-amber-50/30'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-bold text-lg">{request.email}</span>
                                {request.resolved ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                                    Resolved
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                                    Pending
                                  </span>
                                )}
                                {request.account_locked && (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                    Account Locked
                                  </span>
                                )}
                                {!request.user_exists && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">
                                    User Not Found
                                  </span>
                                )}
                              </div>
                              
                              {request.description && (
                                <p className="text-sm text-muted-foreground mb-3 italic">
                                  &quot;{request.description}&quot;
                                </p>
                              )}
                              
                              <div className="text-xs text-muted-foreground space-y-1">
                                <p>Submitted: {new Date(request.created_at).toLocaleString()}</p>
                                {request.resolved && request.resolved_by && (
                                  <p>Resolved by: {request.resolved_by} on {new Date(request.resolved_at).toLocaleString()}</p>
                                )}
                                {request.notes && (
                                  <p>Notes: {request.notes}</p>
                                )}
                              </div>
                            </div>
                            
                            {!request.resolved && (
                              <div className="flex gap-2 ml-4">
                                {request.account_locked && request.user_exists && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleUnlockFromHelp(request.id)}
                                    disabled={processingHelp === request.id}
                                    className="rounded-full bg-blue-500 hover:bg-blue-600"
                                  >
                                    {processingHelp === request.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <Unlock className="w-4 h-4 mr-1" />
                                        Unlock
                                      </>
                                    )}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleResolveHelp(request.id)}
                                  disabled={processingHelp === request.id}
                                  className="rounded-full"
                                >
                                  {processingHelp === request.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Check className="w-4 h-4 mr-1" />
                                      Resolve
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteHelp(request.id)}
                                  disabled={processingHelp === request.id}
                                  className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Feedback View */}
              {view === 'feedback' && (
                <div className="bg-card rounded-2xl border border-border/50 shadow-lg overflow-hidden">
                  <div className="p-6 border-b border-border/50 bg-gradient-to-r from-purple-500/10 to-pink-500/10">
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      <MessageSquare className="w-6 h-6 text-purple-500" />
                      User Feedback
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Review feedback submitted by users about issues and suggestions
                    </p>
                  </div>

                  {loadingFeedback ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-purple-500" />
                      <p className="mt-2 text-muted-foreground">Loading feedback...</p>
                    </div>
                  ) : feedbackList.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No feedback submitted yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border/50">
                      {feedbackList.map((feedback) => (
                        <div key={feedback.id} className="p-6 hover:bg-muted/30 transition-colors">
                          <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                              <p className="font-semibold text-foreground">{feedback.user_email}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(feedback.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                              feedback.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : feedback.status === 'reviewed'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            }`}>
                              {feedback.status === 'pending' && 'Pending'}
                              {feedback.status === 'reviewed' && 'Reviewed'}
                              {feedback.status === 'implemented' && 'Implemented'}
                            </span>
                          </div>

                          {feedback.issues && (
                            <div className="mb-3 p-3 bg-red-50 dark:bg-red-950/20 rounded-xl">
                              <p className="text-xs font-semibold text-red-600 uppercase mb-1">Issues Reported</p>
                              <p className="text-sm text-foreground whitespace-pre-line">{feedback.issues}</p>
                            </div>
                          )}

                          {feedback.suggestions && (
                            <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-xl">
                              <p className="text-xs font-semibold text-amber-600 uppercase mb-1">Suggestions</p>
                              <p className="text-sm text-foreground whitespace-pre-line">{feedback.suggestions}</p>
                            </div>
                          )}

                          {feedback.admin_response && (
                            <div className="mb-3 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-xl border-l-4 border-purple-500">
                              <p className="text-xs font-semibold text-purple-600 uppercase mb-1">Admin Response</p>
                              <p className="text-sm text-foreground">{feedback.admin_response}</p>
                            </div>
                          )}

                          {feedback.status === 'pending' && (
                            <div className="flex flex-wrap gap-2 mt-4">
                              <Button
                                size="sm"
                                onClick={() => handleReviewFeedback(feedback.id, 'reviewed')}
                                disabled={processingFeedback === feedback.id}
                                className="rounded-full bg-blue-500 hover:bg-blue-600"
                              >
                                {processingFeedback === feedback.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-4 h-4 mr-1" />
                                    Mark Reviewed
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleReviewFeedback(feedback.id, 'implemented')}
                                disabled={processingFeedback === feedback.id}
                                className="rounded-full bg-green-500 hover:bg-green-600"
                              >
                                {processingFeedback === feedback.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-4 h-4 mr-1" />
                                    Mark Implemented
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteFeedback(feedback.id)}
                                disabled={processingFeedback === feedback.id}
                                className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          )}
        </motion.div>
      </main>
      
      {/* Delete User Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-amber-600 flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5" />
              Delete User Account
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              Are you sure you want to delete <strong>{userToDelete?.email}</strong>?
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 space-y-1">
              <p className="text-sm text-green-700 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Account will be soft-deleted (recoverable)
              </p>
              <p className="text-sm text-green-700 flex items-center gap-2">
                <Check className="w-4 h-4" />
                All data preserved in database
              </p>
              <p className="text-sm text-green-700 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Cloudinary backup preserved (until purged)
              </p>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              You can restore this user later from the Backups tab.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                data-testid="cancel-delete-user"
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setUserToDelete(null);
                }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-delete-user"
                variant="destructive"
                onClick={confirmDeleteUser}
                disabled={deletingUser}
                className="rounded-full"
              >
                {deletingUser ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete User
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminPhotoAccessContext.Provider>
  );
};

export default AdminDashboard;
